import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import { Loader2, Trash2, Eye, Users, FileText, Send, ArrowLeft, Lock, MessageSquare, UserCheck, Handshake, BookOpen, Plus, Save, X, ChevronDown, ChevronUp, DollarSign, MapPin, Bot, Building2, TrendingUp, AlertTriangle, Brain, Lightbulb, LayoutList, ThumbsDown, ThumbsUp, Layers, Ban, Crown, Shield, ToggleLeft, ToggleRight } from "lucide-react";
import type { SubscriptionPlan, SubscriptionFeature, SupplierSubscription, SupplierFeatureOverride } from "@/lib/featureCatalog";
import { FEATURE_CATEGORIES } from "@/lib/featureCatalog";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { getEngineLabel } from "@/config/services";
import { includesServiceSlug, isFacadeServiceSlug, resolveSlug } from "@/config/serviceCatalog";
import { AVAILABLE_PLUGINS } from "@/services/pluginResolver";
import { lovable } from "@/integrations/lovable";

const ADMIN_EMAILS = ["info@cenik.ai", "adam.cernohous@gmail.com"];

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) return session.access_token;
  const { data: { session: fresh } } = await supabase.auth.refreshSession();
  return fresh?.access_token ?? null;
}

interface Inquiry {
  id: string;
  title: string;
  description: string | null;
  region: string | null;
  status: string;
  created_at: string;
  user_id: string | null;
  guest_email: string | null;
  project_id: string | null;
}

interface Supplier {
  id: string;
  company_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  region: string | null;
  user_id: string;
  created_at: string;
  premium_modules: string[];
  enabled_services: string[];
  enabled_plugins: string[];
}

interface Quote {
  id: string;
  inquiry_id: string;
  supplier_id: string;
  total_price: number;
  customer_approved: boolean;
  created_at: string;
  notes: string | null;
}

interface Project {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
}

interface ChatMessage {
  id: string;
  project_id: string;
  role: string;
  content: string;
  created_at: string;
}

interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  phone: string | null;
  created_at: string;
}

interface AuthUser {
  id: string;
  email: string;
  created_at: string;
}

interface KnowledgeEntry {
  id: string;
  category: string;
  title: string;
  content: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface PriceItem {
  id: string;
  supplier_id: string;
  insulation_type: string;
  thickness_cm: number;
  plaster_type: string;
  price_per_m2: number;
  includes_labor: boolean;
  includes_material: boolean;
  includes_scaffolding: boolean;
  is_active: boolean;
  notes: string | null;
  created_at: string;
}

interface LocationPref {
  id: string;
  supplier_id: string;
  label: string;
  lat: number;
  lng: number;
  radius_km: number;
  is_whole_country: boolean;
}

interface AiLearning {
  id: string;
  type: string;
  topic: string;
  content: string;
  source: string;
  engine_id: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface GuestChat {
  id: string;
  session_id: string;
  messages: any[];
  created_at: string;
  updated_at: string;
}

interface SupplierChatSample {
  id: string;
  created_at: string;
  session_id: string | null;
  supplier_id: string;
  messages: { role: string; content: string }[];
  assistant_content: string | null;
  price_low: number | null;
  price_high: number | null;
  area_m2: number | null;
  suppliers: { company_name: string } | null;
}

type Tab = "inquiries" | "users" | "chats" | "knowledge" | "prices" | "landing" | "feedback" | "subscriptions";

interface MessageFeedbackEntry {
  id: string;
  project_id: string | null;
  session_id: string | null;
  user_id: string | null;
  engine_id: string;
  message_content: string;
  preceding_question: string | null;
  rating: -1 | 1;
  correction: string | null;
  is_ignored: boolean;
  created_at: string;
}

interface LandingQuery {
  id: string;
  query_text: string;
  service_id: string | null;
  is_new_service: boolean;
  created_at: string;
}

interface ServiceDetection {
  id: string;
  query_text: string;
  session_id: string | null;
  source: string;
  keyword_match_slug: string | null;
  keyword_match_confidence: number | null;
  keyword_match_details: { matchedKeywords?: string[]; score?: number; maxScore?: number } | null;
  ai_match_slug: string | null;
  ai_match_confidence: number | null;
  ai_match_is_new: boolean;
  ai_suggested_service_name: string | null;
  outcome: "matched" | "uncertain" | "new_service_suggested";
  final_service_slug: string | null;
  routing_method: string;
  routing_reason: string | null;
  corrected_service_slug: string | null;
  corrected_at: string | null;
  correction_source: string | null;
  admin_reviewed: boolean;
  admin_reviewed_at: string | null;
  admin_action: string | null;
  landing_query_id: string | null;
  created_at: string;
}

interface Service {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  keywords: string[];
  query_count: number;
  status: "active" | "candidate" | "rejected" | "queued";
  created_by_ai: boolean;
  created_at: string;
}

interface CostField {
  key: string;
  label: string;
  icon: string;
  hint?: string | null;
}

interface QuoteConfig {
  id: string;
  service_id: string;
  cost_fields: CostField[];
  show_profit: boolean;
  show_vat: boolean;
  unit_label: string;
  status: "draft" | "approved";
  created_at: string;
  updated_at: string;
}

export default function AdminDashboard() {
  const { t } = useI18n();
  const { user, isReady } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Auth state
  const [adminAuthed, setAdminAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Data state
  const [loading, setLoading] = useState(false);
  const validTabs: Tab[] = ["inquiries", "users", "chats", "knowledge", "prices", "landing", "feedback", "subscriptions"];
  const tabParam = searchParams.get("tab") as Tab | null;
  const tab: Tab = tabParam && validTabs.includes(tabParam) ? tabParam : "inquiries";
  const setTab = (t: Tab) => setSearchParams({ tab: t }, { replace: true });
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [guestChats, setGuestChats] = useState<GuestChat[]>([]);
  const [supplierChats, setSupplierChats] = useState<SupplierChatSample[]>([]);
  const [expandedSupplierChat, setExpandedSupplierChat] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [authUsers, setAuthUsers] = useState<AuthUser[]>([]);
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null);
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [chatFilter, setChatFilter] = useState<"all" | "sent" | "not_sent">("all");
  const [chatSubTab, setChatSubTab] = useState<"chats" | "feedback" | "supplier-chats">("chats");
  const [usersSubTab, setUsersSubTab] = useState<"customers" | "suppliers">("customers");
  const [knowledge, setKnowledge] = useState<KnowledgeEntry[]>([]);
  const [editingKnowledge, setEditingKnowledge] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [expandedKnowledge, setExpandedKnowledge] = useState<string | null>(null);
  // Quote config state (read-only for service card badges)
  const [quoteConfigs, setQuoteConfigs] = useState<QuoteConfig[]>([]);
  
  // Prices tab state
  const [priceItems, setPriceItems] = useState<PriceItem[]>([]);
  const [locationPrefs, setLocationPrefs] = useState<LocationPref[]>([]);
  const [priceFilterType, setPriceFilterType] = useState<string>("all");
  const [priceFilterRegion, setPriceFilterRegion] = useState<string>("all");
  const [priceSubTab, setPriceSubTab] = useState<"comparison" | "supplier_prices">("comparison");
  const [landingQueries, setLandingQueries] = useState<LandingQuery[]>([]);
  const [serviceDetections, setServiceDetections] = useState<ServiceDetection[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [showCreateService, setShowCreateService] = useState(false);
  const [newSvcName, setNewSvcName] = useState("");
  const [newSvcSlug, setNewSvcSlug] = useState("");
  const [newSvcKeywords, setNewSvcKeywords] = useState("");
  const [newSvcSaving, setNewSvcSaving] = useState(false);
  const [messageFeedback, setMessageFeedback] = useState<MessageFeedbackEntry[]>([]);
  const [feedbackIgnoringId, setFeedbackIgnoringId] = useState<string | null>(null);

  // Subscription system state
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptionFeatures, setSubscriptionFeatures] = useState<SubscriptionFeature[]>([]);
  const [planFeatures, setPlanFeatures] = useState<{ plan_id: string; feature_id: string }[]>([]);
  const [supplierSubscriptions, setSupplierSubscriptions] = useState<SupplierSubscription[]>([]);
  const [supplierFeatureOverrides, setSupplierFeatureOverrides] = useState<SupplierFeatureOverride[]>([]);
  const [expandedSubscriptionSupplier, setExpandedSubscriptionSupplier] = useState<string | null>(null);

  const normalizeServiceSlug = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 50);

  // Learnings tab state
  const [learnings, setLearnings] = useState<AiLearning[]>([]);
  const [newLearningType, setNewLearningType] = useState("correction");
  const [newLearningTopic, setNewLearningTopic] = useState("");
  const [newLearningContent, setNewLearningContent] = useState("");
  const [newLearningEngineId, setNewLearningEngineId] = useState<string>("");
  const [showNewLearning, setShowNewLearning] = useState(false);
  const [showNewKnowledge, setShowNewKnowledge] = useState(false);
  const [newKnowledgeCategory, setNewKnowledgeCategory] = useState("");
  const [newKnowledgeTitle, setNewKnowledgeTitle] = useState("");
  const [newKnowledgeContent, setNewKnowledgeContent] = useState("");
  const [classifyingKnowledge, setClassifyingKnowledge] = useState(false);

  // Inline correction state (chat tab)
  const [correctingMessageId, setCorrectingMessageId] = useState<string | null>(null);
  const [correctionText, setCorrectionText] = useState("");
  const [correctionSaving, setCorrectionSaving] = useState(false);

  const handleInlineCorrection = async (aiMessage: { id: string; content: string; role: string; }, chatMessages: { id: string; content: string; role: string; }[]) => {
    if (!correctionText.trim()) {
      toast.error(t("admin.toast.writeCorrectedAnswer"));
      return;
    }
    setCorrectionSaving(true);
    // Find the user message that preceded this AI message
    const msgIndex = chatMessages.findIndex(m => m.id === aiMessage.id);
    const precedingUserMsg = chatMessages.slice(0, msgIndex).reverse().find(m => m.role === "user");

    const topic = `Oprava: ${precedingUserMsg?.content.slice(0, 60) || "AI odpověď"}`;
    const content = `Když se zákazník zeptá: "${precedingUserMsg?.content.slice(0, 300) || "—"}"\n\nAI ŠPATNĚ odpověděla: "${aiMessage.content.slice(0, 300)}"\n\nSPRÁVNÁ odpověď by měla být:\n${correctionText}`;

    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "create_learning",
          entry: { type: "correction", topic, content, source: "chat_review" },
        }),
      });
      if (!resp.ok) throw new Error("Create failed");
      const result = await resp.json();
      if (result.data) setLearnings(prev => [...prev, result.data]);
      setCorrectingMessageId(null);
      setCorrectionText("");
      toast.success(t("admin.toast.correctionSaved"));
    } catch {
      toast.error(t("admin.toast.correctionSaveFailed"));
    } finally {
      setCorrectionSaving(false);
    }
  };

  /** Vyřadit feedback – ochrana před záměrně špatnými hodnoceními */
  const handleIgnoreFeedback = async (fb: MessageFeedbackEntry) => {
    setFeedbackIgnoringId(fb.id);
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "ignore_feedback", id: fb.id }),
      });
      if (!resp.ok) throw new Error("Failed");
      setMessageFeedback((prev) => prev.map((f) => f.id === fb.id ? { ...f, is_ignored: true } : f));
      toast.success(t("admin.toast.feedbackIgnored"));
    } catch {
      toast.error(t("admin.toast.feedbackIgnoreFailed"));
    } finally {
      setFeedbackIgnoringId(null);
    }
  };

  // Check if already logged in as admin
  useEffect(() => {
    if (!isReady) return;
    if (user && ADMIN_EMAILS.includes(user.email || "")) {
      setAdminAuthed(true);
    }
  }, [isReady, user]);

  // Load data when authed
  useEffect(() => {
    if (adminAuthed) loadAllData();
  }, [adminAuthed]);

  // Auto-classify unmatched landing queries via detect-intent
  const autoClassifyRan = useRef(false);
  useEffect(() => {
    if (autoClassifyRan.current || landingQueries.length === 0 || services.length === 0) return;
    const unmatched = landingQueries.filter(q => !q.service_id && q.query_text);
    if (unmatched.length === 0) return;
    autoClassifyRan.current = true;

    (async () => {
      for (const q of unmatched) {
        try {
          const { data } = await supabase.functions.invoke("detect-intent", { body: { query: q.query_text } });
          const serviceId = data?.serviceId || null;
          const isNew = data?.isNewService || false;
          if (serviceId) {
            const token = await getToken();
            await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
              body: JSON.stringify({ action: "update_landing_query_service", id: q.id, serviceId }),
            });
            setLandingQueries(prev => prev.map(lq => lq.id === q.id ? { ...lq, service_id: serviceId, is_new_service: isNew } : lq));
            if (isNew) {
              const token2 = await getToken();
              const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
                headers: { Authorization: `Bearer ${token2}`, "Content-Type": "application/json" },
              });
              const fresh = await resp.json();
              if (fresh.services) setServices(fresh.services);
            }
          }
        } catch { /* skip failed classify */ }
      }
    })();
  }, [landingQueries, services]);

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);

    // Check if email is in admin whitelist first
    if (!ADMIN_EMAILS.includes(email)) {
      setLoginLoading(false);
      toast.error(t("admin.noPermission"));
      return;
    }

    // Sign out any existing session first to ensure clean admin login
    await supabase.auth.signOut();

    // Try to sign in first
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      // If credentials invalid, try to register (account doesn't exist yet)
      if (error.message.includes("Invalid login credentials")) {
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({ 
          email, 
          password 
        });
        
        if (signUpError) {
          setLoginLoading(false);
          toast.error(t("admin.registrationFailed") + ": " + signUpError.message);
          return;
        }

        if (signUpData.user) {
          setLoginLoading(false);
          setAdminAuthed(true);
          toast.success(t("admin.accountCreated"));
          return;
        }
      }

      setLoginLoading(false);
      toast.error(t("admin.loginFailed") + ": " + error.message);
      return;
    }

    setLoginLoading(false);
    setAdminAuthed(true);
  };

  const handleGoogleLogin = async () => {
    setLoginLoading(true);
    const { error } = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin + "/admin-secret",
    });
    if (error) {
      setLoginLoading(false);
      toast.error(t("admin.googleLoginFailed") + ": " + error.message);
    }
    // After redirect back, the useEffect [isReady, user] will auto-detect admin email
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) {
        setAdminAuthed(false);
        toast.error(t("admin.sessionExpired"));
        return;
      }
      const baseUrl = import.meta.env.VITE_SUPABASE_URL;

      const resp = await fetch(`${baseUrl}/functions/v1/admin-data`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (resp.status === 401 || resp.status === 403) {
        setAdminAuthed(false);
        toast.error(t("admin.loginExpired"));
        return;
      }
      if (!resp.ok) throw new Error("Failed to load admin data");

      const data = await resp.json();
      setInquiries(data.inquiries || []);
      setSuppliers(data.suppliers || []);
      setQuotes(data.quotes || []);
      setProjects(data.projects || []);
      setChatMessages(data.chatMessages || []);
      setProfiles(data.profiles || []);
      setAuthUsers(data.authUsers || []);
      setKnowledge(data.knowledge || []);
      setPriceItems(data.priceItems || []);
      setLocationPrefs(data.locationPrefs || []);
      setLearnings(data.learnings || []);
      setGuestChats(data.guestChats || []);
      setSupplierChats(data.supplierChats || []);
      setLandingQueries(data.landingQueries || []);
      setServiceDetections(data.serviceDetections || []);
      setServices(data.services || []);
      setMessageFeedback(data.messageFeedback || []);
      setQuoteConfigs(data.quoteConfigs || []);
      // Subscription system
      setSubscriptionPlans(data.subscriptionPlans || []);
      setSubscriptionFeatures(data.subscriptionFeatures || []);
      setPlanFeatures(data.planFeatures || []);
      setSupplierSubscriptions(data.supplierSubscriptions || []);
      setSupplierFeatureOverrides(data.supplierFeatureOverrides || []);
    } catch (err) {
      console.error("Admin data load error:", err);
      toast.error(t("admin.toast.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInquiry = async (id: string) => {
    if (!confirm(t("admin.confirm.deleteInquiry"))) return;
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "delete_inquiry", id }),
      });
      if (!resp.ok) throw new Error("Delete failed");
      setInquiries((prev) => prev.filter((i) => i.id !== id));
      if (selectedInquiry?.id === id) setSelectedInquiry(null);
      toast.success(t("admin.toast.inquiryDeleted"));
    } catch {
      toast.error(t("admin.toast.deleteFailed"));
    }
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "update_inquiry_status", id, status }),
      });
      if (!resp.ok) throw new Error("Update failed");
      setInquiries((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
      toast.success(t("admin.toast.statusUpdated"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setAdminAuthed(false);
    setEmail("");
    setPassword("");
  };

  const handleCreateKnowledge = async () => {
    if (!newKnowledgeTitle.trim() || !newKnowledgeContent.trim()) return;
    try {
      const token = await getToken();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "create_knowledge", entry: { title: newKnowledgeTitle, category: newKnowledgeCategory || "obecné", content: newKnowledgeContent } }),
      });
      const data = await res.json();
      if (data.data) setKnowledge(prev => [data.data, ...prev]);
      setShowNewKnowledge(false);
      setNewKnowledgeCategory("");
      setNewKnowledgeTitle("");
      setNewKnowledgeContent("");
      toast.success(t("admin.toast.knowledgeAdded"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  const handleClassifyKnowledge = async () => {
    if (!newKnowledgeContent.trim()) return;
    setClassifyingKnowledge(true);
    try {
      const token = await getToken();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "classify_knowledge", content: newKnowledgeContent }),
      });
      const data = await res.json();
      if (data.title) setNewKnowledgeTitle(data.title);
      if (data.category) setNewKnowledgeCategory(data.category);
      toast.success(t("admin.toast.aiCategorized"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    } finally {
      setClassifyingKnowledge(false);
    }
  };

  const handleSaveKnowledge = async (entry: KnowledgeEntry) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "update_knowledge",
          knowledgeId: entry.id,
          updates: { title: editTitle, category: editCategory, content: editContent },
        }),
      });
      if (!resp.ok) throw new Error("Update failed");
      setKnowledge(prev => prev.map(k => k.id === entry.id ? { ...k, title: editTitle, category: editCategory, content: editContent } : k));
      setEditingKnowledge(null);
      toast.success(t("admin.toast.knowledgeSaved"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  const handleToggleKnowledge = async (entry: KnowledgeEntry) => {
    try {
      const token = await getToken();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "update_knowledge",
          knowledgeId: entry.id,
          updates: { is_active: !entry.is_active },
        }),
      });
      setKnowledge(prev => prev.map(k => k.id === entry.id ? { ...k, is_active: !k.is_active } : k));
      toast.success(t(entry.is_active ? "admin.toast.deactivated" : "admin.toast.activated"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  const handleDeleteKnowledge = async (id: string) => {
    if (!confirm(t("admin.confirm.deleteLearning"))) return;
    try {
      const token = await getToken();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "delete_knowledge", id }),
      });
      setKnowledge(prev => prev.filter(k => k.id !== id));
      toast.success(t("admin.toast.deleted"));
    } catch {
      toast.error(t("admin.toast.deleteFailed"));
    }
  };

  // Learning CRUD handlers
  const handleCreateLearning = async () => {
    if (!newLearningTopic.trim() || !newLearningContent.trim()) {
      toast.error(t("admin.toast.fillTopicContent"));
      return;
    }
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "create_learning",
          entry: { type: newLearningType, topic: newLearningTopic, content: newLearningContent, source: "admin", engine_id: newLearningEngineId || null },
        }),
      });
      if (!resp.ok) throw new Error("Create failed");
      const result = await resp.json();
      if (result.data) setLearnings(prev => [...prev, result.data]);
      setNewLearningTopic("");
      setNewLearningContent("");
      setNewLearningEngineId("");
      setShowNewLearning(false);
      toast.success(t("admin.toast.learningAdded"));
    } catch {
      toast.error(t("admin.toast.addFailed"));
    }
  };

  const handleToggleLearning = async (entry: AiLearning) => {
    try {
      const token = await getToken();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: "update_learning",
          learningId: entry.id,
          updates: { is_active: !entry.is_active },
        }),
      });
      setLearnings(prev => prev.map(l => l.id === entry.id ? { ...l, is_active: !l.is_active } : l));
      toast.success(t(entry.is_active ? "admin.toast.deactivated" : "admin.toast.activated"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  const togglePremiumModule = async (supplierId: string, module: string) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "toggle_premium_module", supplierId, module }),
      });
      const result = await resp.json();
      if (!resp.ok || !result.ok) { toast.error(t("admin.toast.saveFailed")); return; }
      setSuppliers(prev => prev.map(s => s.id === supplierId ? { ...s, premium_modules: result.premium_modules } : s));
      toast.success(result.premium_modules.includes(module) ? t("admin.toast.activated") : t("admin.toast.deactivated"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  const toggleEnabledService = async (supplierId: string, service: string) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "toggle_enabled_service", supplierId, service }),
      });
      const result = await resp.json();
      if (!resp.ok || !result.ok) { toast.error(t("admin.toast.saveFailed")); return; }
      setSuppliers(prev => prev.map(s => s.id === supplierId ? { ...s, enabled_services: result.enabled_services } : s));
      toast.success(t(result.enabled_services.includes(service) ? "admin.toast.activated" : "admin.toast.deactivated"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  const toggleEnabledPlugin = async (supplierId: string, plugin: string) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "toggle_enabled_plugin", supplierId, plugin }),
      });
      const result = await resp.json();
      if (!resp.ok || !result.ok) { toast.error(t("admin.toast.saveFailed")); return; }
      setSuppliers(prev => prev.map(s => s.id === supplierId ? { ...s, enabled_plugins: result.enabled_plugins } : s));
      toast.success(t(result.enabled_plugins.includes(plugin) ? "admin.toast.activated" : "admin.toast.deactivated"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  const handleCreateService = async () => {
    const name = newSvcName.trim();
    const slug = normalizeServiceSlug(newSvcSlug.trim());

    if (!name || !newSvcSlug.trim()) {
      toast.error(t("admin.toast.fillNameSlug"));
      return;
    }
    if (!slug) {
      toast.error(t("admin.toast.invalidSlug"));
      return;
    }

    setNewSvcSaving(true);
    try {
      const token = await getToken();
      const keywords = newSvcKeywords.split(",").map(k => k.trim()).filter(Boolean);
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "create_service", name, slug, keywords }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.ok) throw new Error(typeof result.error === "string" ? result.error : t("admin.toast.createServiceFailed"));
      toast.success(t("admin.toast.serviceCreated"));
      setShowCreateService(false);
      setNewSvcName(""); setNewSvcSlug(""); setNewSvcKeywords("");
      await loadAllData();
    } catch (error) {
      const message = error instanceof Error ? error.message : t("admin.toast.createServiceFailed");
      toast.error(message);
    } finally {
      setNewSvcSaving(false);
    }
  };

  const handleServiceStatus = async (id: string, status: "active" | "candidate" | "rejected" | "queued") => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "update_service_status", id, status }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.ok) throw new Error("Failed");
      setServices(prev => prev.map(s => s.id === id ? { ...s, status } : s));
      toast.success(t(status === "active" ? "admin.toast.serviceActivated" : status === "rejected" ? "admin.toast.serviceRejected" : status === "queued" ? "admin.toast.movedToQueue" : "admin.toast.movedToCandidate"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  // --- Quote config handlers ---
  const handleClassifyLandingQuery = async (queryId: string, queryText: string) => {
    try {
      const { data } = await supabase.functions.invoke("detect-intent", { body: { query: queryText } });
      const serviceId = data?.serviceId || null;
      const isNew = data?.isNewService || false;
      if (serviceId) {
        const token = await getToken();
        const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ action: "update_landing_query_service", id: queryId, serviceId }),
        });
        const result = await resp.json().catch(() => ({}));
        if (!resp.ok || !result.ok) throw new Error("Failed");
        setLandingQueries(prev => prev.map(q => q.id === queryId ? { ...q, service_id: serviceId, is_new_service: isNew } : q));
        if (isNew) {
          // Refresh to pick up the new candidate service
          const token2 = await getToken();
          const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
            headers: { Authorization: `Bearer ${token2}`, "Content-Type": "application/json" },
          });
          const fresh = await resp.json();
          if (fresh.services) setServices(fresh.services);
        }
        toast.success(t(isNew ? "admin.toast.newServiceProposed" : "admin.toast.serviceAssigned"));
      } else {
        toast.info(t("admin.toast.aiNoMatch"));
      }
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  const handleUpdateLandingQueryService = async (queryId: string, serviceId: string | null) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_landing_query_service", id: queryId, serviceId }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.ok) throw new Error("Failed");
      setLandingQueries(prev => prev.map(q => q.id === queryId ? { ...q, service_id: serviceId } : q));
      toast.success(t("admin.toast.serviceAssigned"));
    } catch {
      toast.error(t("admin.toast.saveFailed"));
    }
  };

  // ============================================================
  // Service Detection: Correction handlers
  // ============================================================
  const handleCorrectDetection = async (detectionId: string, correctedSlug: string) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "correct_detection", detectionId, correctedSlug }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.ok) throw new Error("Failed");
      setServiceDetections(prev => prev.map(d =>
        d.id === detectionId
          ? { ...d, corrected_service_slug: correctedSlug, corrected_at: new Date().toISOString(), admin_reviewed: true, admin_action: "assigned_existing" }
          : d
      ));
      toast.success(t("admin.toast.detectionFixed"));
    } catch {
      toast.error(t("admin.toast.detectionFixFailed"));
    }
  };

  const handleDismissDetection = async (detectionId: string) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "dismiss_detection", detectionId }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.ok) throw new Error("Failed");
      setServiceDetections(prev => prev.map(d =>
        d.id === detectionId
          ? { ...d, admin_reviewed: true, admin_action: "dismissed" }
          : d
      ));
      toast.success(t("admin.toast.detectionRejected"));
    } catch {
      toast.error(t("admin.toast.detectionRejectFailed"));
    }
  };

  const handleCreateServiceFromDetection = async (detectionId: string, suggestedName: string) => {
    const name = prompt(t("admin.placeholder.newServiceName"), suggestedName);
    if (!name) return;
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "create_service_from_detection", detectionId, serviceName: name }),
      });
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || !result.ok) throw new Error("Failed");
      setServiceDetections(prev => prev.map(d =>
        d.id === detectionId
          ? { ...d, admin_reviewed: true, admin_action: "created_new", final_service_slug: result.slug }
          : d
      ));
      // Refresh services
      const token2 = await getToken();
      const freshResp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        headers: { Authorization: `Bearer ${token2}`, "Content-Type": "application/json" },
      });
      const fresh = await freshResp.json();
      if (fresh.services) setServices(fresh.services);
      toast.success(t("admin.toast.serviceCreated"));
    } catch {
      toast.error(t("admin.toast.createServiceFailed"));
    }
  };

  const handleDeleteLearning = async (id: string) => {
    if (!confirm(t("admin.confirm.deleteLearning"))) return;
    try {
      const token = await getToken();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ action: "delete_learning", id }),
      });
      setLearnings(prev => prev.filter(l => l.id !== id));
      toast.success(t("admin.toast.deleted"));
    } catch {
      toast.error(t("admin.toast.deleteFailed"));
    }
  };

  // --- Admin Login Screen ---
  if (!adminAuthed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center space-y-2">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Lock className="w-6 h-6 text-primary" />
              </div>
            </div>
            <h1 className="text-xl font-bold text-foreground">{t("admin.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("admin.loginPrompt")}</p>
          </div>

          <button
            onClick={handleGoogleLogin}
            disabled={loginLoading}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm font-semibold hover:bg-muted transition-colors disabled:opacity-50"
          >
            {loginLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                {t("admin.googleLogin")}
              </>
            )}
          </button>

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex-1 h-px bg-border" />
            <span>{t("admin.orPassword")}</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <form onSubmit={handleAdminLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("admin.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground outline-none focus:ring-2 focus:ring-ring"
                placeholder="admin@example.com"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">{t("admin.password")}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground outline-none focus:ring-2 focus:ring-ring"
                placeholder="••••••••"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loginLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("admin.loginButton")}
            </button>
          </form>

          <button
            onClick={() => navigate("/")}
            className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Zpět na hlavní stránku
          </button>
        </div>
      </div>
    );
  }

  // --- Admin Dashboard ---
  // Derive chat data: group messages by project, classify by inquiry sent or not
  const projectInquiryMap = new Set(inquiries.filter(i => i.project_id).map(i => i.project_id!));
  
  const loggedInChats = projects.map(p => ({
    ...p,
    messages: chatMessages.filter(m => m.project_id === p.id),
    hasInquiry: projectInquiryMap.has(p.id),
    isGuest: false
  })).filter(p => p.messages.length > 0);

  // Guest inquiries (no user_id) - match to guest chats by time proximity
  const guestInquiries = inquiries.filter(i => !i.user_id && i.guest_email);

  const formattedGuestChats = guestChats.map(g => {
    // Check if any guest inquiry was created within 30 min of the chat session
    const chatTime = new Date(g.updated_at).getTime();
    const matchedInquiry = guestInquiries.find(i => {
      const inqTime = new Date(i.created_at).getTime();
      return Math.abs(inqTime - chatTime) < 30 * 60 * 1000; // 30 min window
    });
    return {
      id: g.id,
      name: matchedInquiry
        ? `Guest: ${matchedInquiry.guest_email} – ${matchedInquiry.title}`
        : "Nepřihlášený uživatel (Guest)",
      user_id: g.session_id,
      created_at: g.created_at,
      messages: g.messages.map((m: any, i: number) => ({ id: `${g.id}-${i}`, role: m.role, content: m.content })),
      hasInquiry: !!matchedInquiry,
      isGuest: true
    };
  }).filter(p => p.messages.length > 0);

  const chatsByProject = [...loggedInChats, ...formattedGuestChats].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const chatsWithInquiry = chatsByProject.filter(c => c.hasInquiry);
  const chatsWithoutInquiry = chatsByProject.filter(c => !c.hasInquiry);

  // Build customers list from all auth users (exclude admins only)
  const supplierUserIds = new Set(suppliers.map(s => s.user_id));
  const customers = authUsers
    .filter(u => !ADMIN_EMAILS.includes(u.email || ""))
    .map(u => {
      const profile = profiles.find(p => p.user_id === u.id);
      const userInquiries = inquiries.filter(i => i.user_id === u.id);
      const userChats = projects.filter(p => p.user_id === u.id);
      const isSupplier = supplierUserIds.has(u.id);
      const supplierInfo = isSupplier ? suppliers.find(s => s.user_id === u.id) : null;
      return { ...u, profile, inquiryCount: userInquiries.length, chatCount: userChats.length, isSupplier, supplierInfo };
    });

  // Unique insulation types for filter
  const insulationTypes = [...new Set(priceItems.map(p => p.insulation_type))];
  
  // Get unique regions from location prefs labels
  const uniqueRegions = [...new Set(locationPrefs.map(l => l.label))];
  
  // Filter price items
  const filteredPriceItems = priceItems.filter(p => {
    if (priceFilterType !== "all" && p.insulation_type !== priceFilterType) return false;
    if (priceFilterRegion !== "all") {
      // Find supplier's locations and check if matches
      const supplierLocs = locationPrefs.filter(l => l.supplier_id === p.supplier_id);
      const hasWholeCZ = supplierLocs.some(l => l.is_whole_country);
      if (!hasWholeCZ && !supplierLocs.some(l => l.label === priceFilterRegion)) return false;
    }
    return true;
  });

  // Group by insulation type for stats
  const priceStats = insulationTypes.map(type => {
    const items = priceItems.filter(p => p.insulation_type === type && p.is_active);
    if (items.length === 0) return null;
    const prices = items.map(i => i.price_per_m2);
    return {
      type,
      count: items.length,
      min: Math.min(...prices),
      max: Math.max(...prices),
      avg: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length),
    };
  }).filter(Boolean);

  // Subscription helpers
  const premiumPlan = subscriptionPlans.find(p => p.key === "premium");
  const premiumSupplierIds = new Set(supplierSubscriptions.filter(s => s.is_active).map(s => s.supplier_id));
  const suppliersWithOverrides = new Set(supplierFeatureOverrides.map(o => o.supplier_id));

  const getSupplierPlanKey = (supplierId: string): string => {
    const sub = supplierSubscriptions.find(s => s.supplier_id === supplierId && s.is_active);
    if (!sub) return "free";
    const plan = subscriptionPlans.find(p => p.id === sub.plan_id);
    return plan?.key || "free";
  };

  const handleSetSupplierPlan = async (supplierId: string, planKey: string) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "set_supplier_plan", supplierId, planKey }),
      });
      if (!resp.ok) throw new Error("Failed");
      if (planKey === "free") {
        setSupplierSubscriptions(prev => prev.filter(s => s.supplier_id !== supplierId));
      } else {
        const plan = subscriptionPlans.find(p => p.key === planKey);
        if (plan) {
          setSupplierSubscriptions(prev => {
            const filtered = prev.filter(s => s.supplier_id !== supplierId);
            return [...filtered, { id: crypto.randomUUID(), supplier_id: supplierId, plan_id: plan.id, activated_at: new Date().toISOString(), activated_by: user?.email || null, expires_at: null, is_active: true, notes: null }];
          });
        }
      }
      toast.success(t("admin.toast.saved"));
    } catch {
      toast.error(t("admin.toast.planChangeFailed"));
    }
  };

  const handleToggleFeatureOverride = async (supplierId: string, featureKey: string, isEnabled: boolean | null) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "toggle_feature_override", supplierId, featureKey, isEnabled }),
      });
      if (!resp.ok) throw new Error("Failed");
      if (isEnabled === null) {
        setSupplierFeatureOverrides(prev => prev.filter(o => !(o.supplier_id === supplierId && o.feature_key === featureKey)));
      } else {
        setSupplierFeatureOverrides(prev => {
          const filtered = prev.filter(o => !(o.supplier_id === supplierId && o.feature_key === featureKey));
          return [...filtered, { id: crypto.randomUUID(), supplier_id: supplierId, feature_key: featureKey, is_enabled: isEnabled, reason: null }];
        });
      }
      toast.success(t("admin.toast.overrideSaved"));
    } catch {
      toast.error(t("admin.toast.overrideSaveFailed"));
    }
  };

  const handleToggleFeatureActive = async (featureId: string, isActive: boolean) => {
    try {
      const token = await getToken();
      const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-data`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_feature_status", featureId, isActive }),
      });
      if (!resp.ok) throw new Error("Failed");
      setSubscriptionFeatures(prev => prev.map(f => f.id === featureId ? { ...f, is_active: isActive } : f));
      toast.success(t(isActive ? "admin.toast.activated" : "admin.toast.deactivated"));
    } catch {
      toast.error(t("admin.toast.featureToggleFailed"));
    }
  };

  const tabs: { id: Tab; label: string; icon: React.ReactNode; count: number }[] = [
    { id: "chats", label: t("admin.tab.chats"), icon: <MessageSquare className="w-4 h-4" />, count: chatsByProject.length },
    { id: "inquiries", label: t("admin.tab.inquiries"), icon: <FileText className="w-4 h-4" />, count: inquiries.length },
    { id: "users" as Tab, label: t("admin.tab.users"), icon: <Users className="w-4 h-4" />, count: customers.length + suppliers.length },
    { id: "prices", label: t("admin.tab.prices"), icon: <DollarSign className="w-4 h-4" />, count: priceItems.length },
    { id: "landing", label: t("admin.tab.services"), icon: <Layers className="w-4 h-4" />, count: services.filter(s => s.status === "candidate").length || services.filter(s => s.status === "active").length },
    { id: "subscriptions", label: t("admin.tab.subscriptions"), icon: <Crown className="w-4 h-4" />, count: premiumSupplierIds.size },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-foreground">{t("admin.title")}</h1>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{user?.email}</span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadAllData} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            Obnovit
          </button>
          <button onClick={handleLogout} className="text-sm text-destructive hover:underline">
            Odhlásit
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="flex justify-center overflow-x-auto scrollbar-hide">
          <div className="flex gap-1 px-4">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 whitespace-nowrap px-3 py-3 text-sm font-medium border-b-2 transition-colors sm:gap-2 sm:px-4 ${
                  tab === t.id
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.icon}
                <span className="hidden sm:inline">{t.label}</span>
                <span className="bg-muted text-muted-foreground text-xs px-1.5 py-0.5 rounded-full">{t.count}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {tab === "inquiries" && (
              <div className="space-y-3">
                {inquiries.length === 0 ? (
                  <p className="text-muted-foreground text-center py-10">{t("admin.empty.inquiries")}</p>
                ) : (
                  inquiries.map((inq) => {
                    const inquiryQuotes = quotes.filter(q => q.inquiry_id === inq.id);
                    return (
                      <div key={inq.id} className="border border-border rounded-xl overflow-hidden">
                        <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-border">
                          {/* Levý sloupec – poptávka */}
                          <div className="p-4">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <div className="flex items-center gap-2 min-w-0">
                                <h3 className="font-medium text-foreground truncate">{inq.title}</h3>
                                <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                                  inq.status === "open" ? "bg-green-500/10 text-green-600" : "bg-muted text-muted-foreground"
                                }`}>
                                  {inq.status}
                                </span>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <select
                                  value={inq.status}
                                  onChange={(e) => handleUpdateStatus(inq.id, e.target.value)}
                                  className="text-xs border border-border rounded-lg px-2 py-1 bg-background text-foreground"
                                >
                                  <option value="open">open</option>
                                  <option value="closed">closed</option>
                                  <option value="in_progress">in_progress</option>
                                </select>
                                <button onClick={() => setSelectedInquiry(inq)} className="p-2 rounded-lg hover:bg-accent text-muted-foreground">
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button onClick={() => handleDeleteInquiry(inq.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground truncate">{inq.description?.slice(0, 100)}</p>
                            <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                              <span>{new Date(inq.created_at).toLocaleDateString("cs")}</span>
                              <span>{inq.region || "—"}</span>
                              <span>{inq.guest_email || inq.user_id?.slice(0, 8) || "—"}</span>
                            </div>
                          </div>
                          {/* Pravý sloupec – nabídky */}
                          <div className="p-4 bg-muted/20">
                            {inquiryQuotes.length === 0 ? (
                              <p className="text-xs text-muted-foreground py-2">Žádné nabídky</p>
                            ) : (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-muted-foreground">{inquiryQuotes.length} {inquiryQuotes.length === 1 ? t("admin.label.offer") : inquiryQuotes.length < 5 ? t("admin.label.offers") : t("admin.label.offersMany")}</p>
                                {inquiryQuotes.map((q) => {
                                  const supplier = suppliers.find((s) => s.id === q.supplier_id);
                                  return (
                                    <div key={q.id} className="flex items-center justify-between gap-2 text-sm">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-foreground truncate">{supplier?.company_name || q.supplier_id.slice(0, 8)}</span>
                                        <span className="text-xs">{q.customer_approved ? "✅" : "⏳"}</span>
                                      </div>
                                      <span className="font-mono text-sm text-foreground shrink-0">{q.total_price.toLocaleString("cs-CZ")} Kč</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {tab === "users" && (
              <div className="space-y-4">
                {/* Sub-taby: Zákazníci / Dodavatelé */}
                <div className="flex gap-1 border-b border-border">
                  <button
                    onClick={() => setUsersSubTab("customers")}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${usersSubTab === "customers" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  >
                    <UserCheck className="w-3.5 h-3.5" />
                    Zákazníci
                    <span className="bg-muted text-muted-foreground text-xs px-1.5 py-0.5 rounded-full">{customers.length}</span>
                  </button>
                  <button
                    onClick={() => setUsersSubTab("suppliers")}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${usersSubTab === "suppliers" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                  >
                    <Handshake className="w-3.5 h-3.5" />
                    Dodavatelé
                    <span className="bg-muted text-muted-foreground text-xs px-1.5 py-0.5 rounded-full">{suppliers.length}</span>
                  </button>
                </div>

                {usersSubTab === "suppliers" && (
                  <div className="space-y-3">
                    {suppliers.length === 0 ? (
                      <p className="text-muted-foreground text-center py-10">{t("admin.empty.suppliers")}</p>
                    ) : (
                      suppliers.map((s) => (
                        <div key={s.id} className="border border-border rounded-xl p-4">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-foreground">{s.company_name}</h3>
                              <span className="text-xs text-muted-foreground">{s.region || "—"}</span>
                            </div>
                            <button
                              onClick={() => togglePremiumModule(s.id, "advanced_pricing")}
                              className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
                                (s.premium_modules ?? []).includes("advanced_pricing")
                                  ? "bg-primary/10 border-primary/30 text-primary font-medium"
                                  : "border-border text-muted-foreground hover:border-primary/30 hover:text-primary"
                              }`}
                            >
                              {(s.premium_modules ?? []).includes("advanced_pricing") ? `✓ ${t("admin.label.advancedCalc")}` : `+ ${t("admin.label.advancedCalc")}`}
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {services.filter(svc => svc.status === "active").map(svc => (
                              <button
                                key={svc.slug}
                                onClick={() => toggleEnabledService(s.id, svc.slug)}
                                className={`text-xs px-2 py-0.5 rounded-md border transition-colors ${
                                  (s.enabled_services ?? []).includes(svc.slug)
                                    ? "bg-green-500/10 border-green-500/30 text-green-700 font-medium"
                                    : "border-border text-muted-foreground hover:border-green-500/30 hover:text-green-700"
                                }`}
                              >
                                {(s.enabled_services ?? []).includes(svc.slug) ? `✓ ${svc.name}` : `+ ${svc.name}`}
                              </button>
                            ))}
                          </div>
                          {/* Plugin toggles — only show for plugins whose service is already enabled */}
                          {AVAILABLE_PLUGINS.filter(p => includesServiceSlug(s.enabled_services ?? [], p.slug)).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            <span className="text-[10px] text-muted-foreground/60 self-center mr-1">Pluginy:</span>
                            {AVAILABLE_PLUGINS
                              .filter(plugin => includesServiceSlug(s.enabled_services ?? [], plugin.slug))
                              .map(plugin => (
                              <button
                                key={plugin.slug}
                                onClick={() => toggleEnabledPlugin(s.id, plugin.slug)}
                                className={`text-xs px-2 py-0.5 rounded-md border transition-colors ${
                                  includesServiceSlug(s.enabled_plugins ?? [], plugin.slug)
                                    ? "bg-purple-500/10 border-purple-500/30 text-purple-700 font-medium"
                                    : "border-border text-muted-foreground hover:border-purple-500/30 hover:text-purple-700"
                                }`}
                              >
                                {includesServiceSlug(s.enabled_plugins ?? [], plugin.slug) ? `✓ ${plugin.label}` : `+ ${plugin.label}`}
                              </button>
                            ))}
                          </div>
                          )}
                          <div className="flex gap-4 text-sm text-muted-foreground mt-1">
                            <span>{s.contact_email || "—"}</span>
                            <span>{s.contact_phone || "—"}</span>
                            <span>{new Date(s.created_at).toLocaleDateString("cs")}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {usersSubTab === "customers" && (
                  <div className="space-y-3">
                    {customers.length === 0 ? (
                      <p className="text-muted-foreground text-center py-10">{t("admin.empty.customers")}</p>
                    ) : (
                      customers.map(c => (
                        <div key={c.id} className="border border-border rounded-xl p-4">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-foreground text-sm">
                                {c.profile?.full_name || c.email}
                              </h3>
                              {c.isSupplier && (
                                <span className="flex items-center gap-1 text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded-full" title={c.supplierInfo?.company_name || "Dodavatel"}>
                                  <Handshake className="w-3 h-3" />
                                  {c.supplierInfo?.company_name || "Dodavatel"}
                                </span>
                              )}
                              {c.profile?.full_name && (
                                <span className="text-xs text-muted-foreground">{c.email}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                {c.inquiryCount} poptávek
                              </span>
                              <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                                {c.chatCount} chatů
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-4 text-xs text-muted-foreground mt-1">
                            <span>{c.profile?.phone || "—"}</span>
                            <span>Registrace: {new Date(c.created_at).toLocaleDateString("cs")}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === "chats" && (() => {
              const filteredChats = chatFilter === "all" ? chatsByProject
                : chatFilter === "sent" ? chatsWithInquiry : chatsWithoutInquiry;
              const negativeFeedback = messageFeedback.filter((f) => f.rating === -1);
              const positiveFeedback = messageFeedback.filter((f) => f.rating === 1);
              return (
                <div className="space-y-4">
                  {/* Sub-navigace: Chaty / Feedback */}
                  <div className="flex gap-1 border-b border-border">
                    <button
                      onClick={() => setChatSubTab("chats")}
                      className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${chatSubTab === "chats" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      Chaty
                      <span className="bg-muted text-muted-foreground text-xs px-1.5 py-0.5 rounded-full">{chatsByProject.length}</span>
                    </button>
                    <button
                      onClick={() => setChatSubTab("feedback")}
                      className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${chatSubTab === "feedback" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                    >
                      <ThumbsDown className="w-3.5 h-3.5" />
                      Feedback
                      {negativeFeedback.length > 0 && <span className="bg-red-500/10 text-red-500 text-xs px-1.5 py-0.5 rounded-full">{negativeFeedback.length}</span>}
                    </button>
                    <button
                      onClick={() => setChatSubTab("supplier-chats")}
                      className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${chatSubTab === "supplier-chats" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                    >
                      <Handshake className="w-3.5 h-3.5" />
                      Chaty firem
                      <span className="bg-muted text-muted-foreground text-xs px-1.5 py-0.5 rounded-full">{supplierChats.length}</span>
                    </button>
                  </div>

                  {chatSubTab === "feedback" && (() => {
                    const activeFeedback = messageFeedback.filter(f => !f.is_ignored);
                    const ignored = messageFeedback.filter(f => f.is_ignored);
                    const activeNeg = activeFeedback.filter(f => f.rating === -1);
                    const activePos = activeFeedback.filter(f => f.rating === 1);
                    const total = activeFeedback.length;
                    const satisfaction = total > 0 ? Math.round((activePos.length / total) * 100) : null;
                    return (
                      <div className="space-y-6">
                        <p className="text-sm text-muted-foreground">{t("admin.section.feedbackHelp")}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="rounded-lg border border-border p-3 bg-muted/30">
                            <div className="text-xs text-muted-foreground">{t("admin.section.totalRatings")}</div>
                            <div className="text-2xl font-bold mt-1">{total}</div>
                          </div>
                          <div className="rounded-lg border border-green-500/20 p-3 bg-green-50/50 dark:bg-green-950/10">
                            <div className="text-xs text-muted-foreground flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {t("admin.status.positive")}</div>
                            <div className="text-2xl font-bold mt-1 text-green-600">{activePos.length}</div>
                          </div>
                          <div className="rounded-lg border border-red-500/20 p-3 bg-red-50/50 dark:bg-red-950/10">
                            <div className="text-xs text-muted-foreground flex items-center gap-1"><ThumbsDown className="w-3 h-3" /> {t("admin.status.negative")}</div>
                            <div className="text-2xl font-bold mt-1 text-red-500">{activeNeg.length}</div>
                          </div>
                          <div className="rounded-lg border border-border p-3 bg-muted/30">
                            <div className="text-xs text-muted-foreground">Spokojenost</div>
                            <div className={`text-2xl font-bold mt-1 ${satisfaction !== null && satisfaction >= 70 ? "text-green-600" : "text-red-500"}`}>{satisfaction !== null ? `${satisfaction} %` : "—"}</div>
                          </div>
                        </div>
                        {activeFeedback.length === 0 ? (
                          <p className="text-muted-foreground text-center py-10">{t("admin.empty.feedback")}</p>
                        ) : (
                          <div className="space-y-3">
                            {[...activeNeg, ...activePos].map((fb) => (
                              <div key={fb.id} className={`border rounded-xl p-4 space-y-3 ${fb.rating === -1 ? "border-red-200 dark:border-red-900/40 bg-red-50/30 dark:bg-red-950/10" : "border-green-200 dark:border-green-900/40 bg-green-50/20 dark:bg-green-950/5"}`}>
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex items-center gap-2">
                                    {fb.rating === -1 ? <ThumbsDown className="w-4 h-4 text-red-500 shrink-0" /> : <ThumbsUp className="w-4 h-4 text-green-600 shrink-0" />}
                                    <span className="text-xs text-muted-foreground">{new Date(fb.created_at).toLocaleString("cs")} · {fb.engine_id}</span>
                                  </div>
                                  <button onClick={() => handleIgnoreFeedback(fb)} disabled={feedbackIgnoringId === fb.id} className="text-xs text-muted-foreground border border-border px-2.5 py-1 rounded-lg hover:border-red-300 hover:text-red-500 disabled:opacity-50 transition-colors shrink-0 flex items-center gap-1.5">
                                    <Ban className="w-3 h-3" />
                                    {feedbackIgnoringId === fb.id ? "…" : t("admin.btn.ignore")}
                                  </button>
                                </div>
                                {fb.preceding_question && (
                                  <div>
                                    <div className="text-xs font-medium text-muted-foreground mb-1">Otázka zákazníka:</div>
                                    <div className="text-sm text-foreground bg-background/60 rounded-lg px-3 py-2 line-clamp-3">{fb.preceding_question}</div>
                                  </div>
                                )}
                                <div>
                                  <div className="text-xs font-medium text-muted-foreground mb-1">Odpověď AI:</div>
                                  <div className="text-sm text-muted-foreground bg-background/60 rounded-lg px-3 py-2 line-clamp-4">{fb.message_content}</div>
                                </div>
                                {fb.correction && (
                                  <div>
                                    <div className="text-xs font-medium text-orange-600 dark:text-orange-400 mb-1">Uživatelova oprava:</div>
                                    <div className="text-sm text-foreground bg-orange-50/50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/40 rounded-lg px-3 py-2">{fb.correction}</div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {ignored.length > 0 && (
                          <p className="text-xs text-muted-foreground text-center">{ignored.length} vyřazeno</p>
                        )}
                      </div>
                    );
                  })()}

                  {chatSubTab === "chats" && <>{/* Workflow banner */}
                  <div className="border border-primary/30 rounded-xl p-4 bg-primary/5 flex items-start gap-3">
                    <Brain className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-foreground mb-1">🔄 Cyklus učení AI</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        <strong>1.</strong> Procházej chaty níže a hledej, kde AI odpověděla špatně nebo nepřesně.{" "}
                        <strong>2.</strong> Klikni na <span className="inline-flex items-center gap-0.5 bg-primary/10 text-primary px-1.5 py-0.5 rounded text-[10px] font-medium"><Brain className="w-3 h-3" /> Vytvořit učení</span> u problematického chatu.{" "}
                        <strong>3.</strong> AI opravu okamžitě zohledňuje v dalších konverzacích – stane se jen lepší! 🚀
                      </p>
                    </div>
                  </div>

                  {/* Filter bar */}
                  <div className="flex gap-2">
                    {([
                      { id: "all" as const, label: t("admin.filter.all"), count: chatsByProject.length },
                      { id: "sent" as const, label: t("admin.filter.sent"), count: chatsWithInquiry.length },
                      { id: "not_sent" as const, label: t("admin.filter.notSent"), count: chatsWithoutInquiry.length },
                    ]).map(f => (
                      <button
                        key={f.id}
                        onClick={() => setChatFilter(f.id)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                          chatFilter === f.id
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-muted-foreground border-border hover:border-foreground/30"
                        }`}
                      >
                        {f.label} ({f.count})
                      </button>
                    ))}
                  </div>

                  {filteredChats.length === 0 ? (
                    <p className="text-muted-foreground text-sm py-10 text-center">Žádné chaty</p>
                  ) : (
                    <div className="space-y-2">
                      {filteredChats.map(chat => {
                        const chatUser = authUsers.find(u => u.id === chat.user_id);
                        const chatProfile = profiles.find(p => p.user_id === chat.user_id);
                        return (
                        <div
                          key={chat.id}
                          className="border border-border rounded-xl p-4 hover:bg-accent/30 transition-colors cursor-pointer"
                          onClick={() => setSelectedChat(selectedChat === chat.id ? null : chat.id)}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-foreground text-sm">{chat.name}</h3>
                              {chatUser && <span className="text-xs text-muted-foreground">{chatProfile?.full_name || chatUser.email}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                chat.hasInquiry ? "bg-green-500/10 text-green-600" : "bg-orange-400/10 text-orange-500"
                              }`}>
                                {chat.hasInquiry ? t("admin.label.inquirySent") : t("admin.label.inquiryNotSent")}
                              </span>
                              <span className="text-xs text-muted-foreground">{chat.messages.length} zpráv</span>
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(chat.created_at).toLocaleDateString("cs")} · {chat.user_id.slice(0, 8)}
                          </div>
                          {selectedChat === chat.id && (
                            <div className="mt-3 border-t border-border pt-3">
                              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                                {chat.messages.map((m, mIdx) => {
                                  const isAi = m.role === "assistant";
                                  const isCorrecting = correctingMessageId === m.id;
                                  return (
                                    <div key={m.id} className="group relative">
                                      <div className={`text-xs p-3 rounded-lg ${!isAi ? "bg-primary/10 text-foreground" : "bg-muted text-muted-foreground"} ${isAi && !isCorrecting ? "cursor-pointer hover:ring-2 hover:ring-primary/30 transition-all" : ""}`}
                                        onClick={(e) => {
                                          if (isAi && !isCorrecting) {
                                            e.stopPropagation();
                                            setCorrectingMessageId(m.id);
                                            setCorrectionText("");
                                          }
                                        }}
                                      >
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="font-semibold">{!isAi ? t("admin.label.user") : t("admin.label.ai")}</span>
                                          {isAi && !isCorrecting && (
                                            <span className="text-[10px] text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                                              <Brain className="w-3 h-3" /> Klikni pro opravu
                                            </span>
                                          )}
                                        </div>
                                        <span className="whitespace-pre-wrap">{m.content.slice(0, 600)}{m.content.length > 600 ? "…" : ""}</span>
                                      </div>
                                      
                                      {/* Inline correction editor */}
                                      {isCorrecting && (
                                        <div className="mt-2 border border-primary/40 rounded-lg p-3 bg-primary/5 space-y-2" onClick={e => e.stopPropagation()}>
                                          <div className="flex items-center gap-2 text-xs font-medium text-primary">
                                            <Brain className="w-3.5 h-3.5" />
                                            Napiš lepší odpověď – AI se z toho okamžitě poučí
                                          </div>
                                          <textarea
                                            value={correctionText}
                                            onChange={e => setCorrectionText(e.target.value)}
                                            className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground min-h-[120px] resize-y"
                                            placeholder="Napiš správnou / lepší odpověď, kterou by AI měla příště použít…"
                                            autoFocus
                                          />
                                          <div className="flex gap-2">
                                            <button
                                              onClick={() => handleInlineCorrection(m, chat.messages)}
                                              disabled={correctionSaving || !correctionText.trim()}
                                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50"
                                            >
                                              {correctionSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                              {t("admin.btn.save")}
                                            </button>
                                            <button
                                              onClick={() => { setCorrectingMessageId(null); setCorrectionText(""); }}
                                              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-muted text-muted-foreground rounded-lg hover:bg-accent"
                                            >
                                              <X className="w-3 h-3" /> {t("admin.btn.cancel")}
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              {/* Saved corrections for this chat */}
                              {learnings.filter(l => l.source === "chat_review").length > 0 && (
                                <div className="mt-3 pt-2 border-t border-border/50">
                                  <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1 mb-1">
                                    <Brain className="w-3 h-3" /> Opravy z tohoto panelu ({learnings.filter(l => l.source === "chat_review").length})
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </>}

                {chatSubTab === "supplier-chats" && (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">{supplierChats.length} zaznamenaných kol konverzace z firemních chatů</p>
                    {supplierChats.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-8 text-center">Zatím žádné chaty z firemních stránek.</p>
                    ) : (
                      <div className="space-y-2">
                        {supplierChats.map((sc) => {
                          const userMsgs = sc.messages.filter(m => m.role === "user");
                          const firstUserMsg = userMsgs[0]?.content || "—";
                          const companyName = sc.suppliers?.company_name || sc.supplier_id.slice(0, 8);
                          const isExpanded = expandedSupplierChat === sc.id;
                          return (
                            <div key={sc.id} className="border border-border rounded-xl overflow-hidden">
                              <button
                                className="w-full flex items-start gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left"
                                onClick={() => setExpandedSupplierChat(isExpanded ? null : sc.id)}
                              >
                                <span className="shrink-0 mt-0.5 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{companyName}</span>
                                <span className="flex-1 text-sm text-foreground truncate">{typeof firstUserMsg === "string" ? firstUserMsg.slice(0, 120) : "—"}</span>
                                <span className="shrink-0 text-xs text-muted-foreground">
                                  {sc.price_low && sc.price_high ? `${sc.price_low}–${sc.price_high} Kč` : sc.price_low ? `od ${sc.price_low} Kč` : ""}
                                  {sc.area_m2 ? ` · ${sc.area_m2} m²` : ""}
                                </span>
                                <span className="shrink-0 text-xs text-muted-foreground">{new Date(sc.created_at).toLocaleString("cs", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                              </button>
                              {isExpanded && (
                                <div className="border-t border-border px-4 py-3 space-y-2 bg-muted/30">
                                  {sc.messages.map((m, i) => (
                                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                      <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-background border border-border text-foreground"}`}>
                                        <p className="whitespace-pre-wrap">{typeof m.content === "string" ? m.content : JSON.stringify(m.content)}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                </div>
              );
            })()}


            {tab === "prices" && (
              <div className="space-y-4">
                {/* Sub-tabs */}
                <div className="flex gap-2 border-b border-border pb-3">
                  <button
                    onClick={() => setPriceSubTab("comparison")}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      priceSubTab === "comparison"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    <TrendingUp className="w-4 h-4" />
                    AI vs Reálné ceny
                  </button>
                  <button
                    onClick={() => setPriceSubTab("supplier_prices")}
                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      priceSubTab === "supplier_prices"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    <Building2 className="w-4 h-4" />
                    Ceníky dodavatelů
                  </button>
                </div>

                {priceSubTab === "comparison" && (() => {
                  // AI hardcoded prices from facadeCalculator.ts
                  const aiPrices = [
                    { type: "EPS bílý", key: "eps_white", aiLow: 1500, aiHigh: 2100, thickness: "14-16 cm" },
                    { type: "EPS šedý", key: "eps_grey", aiLow: 1700, aiHigh: 2400, thickness: "14-16 cm" },
                    { type: "Minerální vata", key: "mineral_wool", aiLow: 2000, aiHigh: 2800, thickness: "14-16 cm" },
                    { type: "Provětrávaná fasáda", key: "vent_facade", aiLow: 2500, aiHigh: 4000, thickness: "—" },
                  ];

                  // Compute real averages from supplier data
                  const realByType = (key: string) => {
                    const items = priceItems.filter(p => p.insulation_type === key && p.is_active);
                    if (items.length === 0) return null;
                    const prices = items.map(i => i.price_per_m2);
                    return {
                      count: items.length,
                      min: Math.round(Math.min(...prices)),
                      max: Math.round(Math.max(...prices)),
                      avg: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length),
                    };
                  };

                  const activePriceCount = priceItems.filter(p => p.is_active).length;
                  const activeSupplierIds = [...new Set(priceItems.filter(p => p.is_active).map(p => p.supplier_id))];

                  return (
                    <div className="space-y-6">
                      {/* Summary cards */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="border border-border rounded-xl p-4 bg-muted/20">
                          <div className="text-xs text-muted-foreground mb-1">Dodavatelů s ceníkem</div>
                          <div className="text-2xl font-bold text-foreground">{activeSupplierIds.length}</div>
                        </div>
                        <div className="border border-border rounded-xl p-4 bg-muted/20">
                          <div className="text-xs text-muted-foreground mb-1">Cenových položek</div>
                          <div className="text-2xl font-bold text-foreground">{activePriceCount}</div>
                        </div>
                        <div className="border border-border rounded-xl p-4 bg-muted/20">
                          <div className="text-xs text-muted-foreground mb-1">Typů materiálů</div>
                          <div className="text-2xl font-bold text-foreground">{insulationTypes.length}</div>
                        </div>
                      </div>

                      {/* Info box */}
                      <div className="border border-primary/20 rounded-xl p-4 bg-primary/5">
                        <div className="flex gap-3 items-start">
                          <Bot className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-foreground space-y-1">
                            <p className="font-medium">Jak AI naceňuje</p>
                            <p className="text-muted-foreground text-xs">
                              AI kombinuje <strong>dvě metody</strong>: 1) Hardcoded rozsahy z kalkulátoru (záchytná síť) a 2) Reálná data z ceníků dodavatelů (přes RPC funkci <code className="text-primary">get_anonymous_pricing</code>). 
                              Pokud jsou k dispozici reálná data, AI je preferuje. Čím víc dodavatelů vyplní ceník, tím přesnější odhady.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Comparison table */}
                      <div className="border border-border rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr className="text-left text-xs text-muted-foreground">
                              <th className="px-4 py-2.5 font-medium">Materiál</th>
                              <th className="px-4 py-2.5 font-medium">
                                <div className="flex items-center gap-1"><Bot className="w-3 h-3" /> AI kalkulátor</div>
                              </th>
                              <th className="px-4 py-2.5 font-medium">
                                <div className="flex items-center gap-1"><Building2 className="w-3 h-3" /> Reálná data</div>
                              </th>
                              <th className="px-4 py-2.5 font-medium">Odchylka</th>
                              <th className="px-4 py-2.5 font-medium">Dodavatelů</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {aiPrices.map(row => {
                              const real = realByType(row.key);
                              const aiMid = (row.aiLow + row.aiHigh) / 2;
                              const realMid = real ? real.avg : null;
                              const deviation = realMid ? Math.round(((realMid - aiMid) / aiMid) * 100) : null;

                              return (
                                <tr key={row.key} className="hover:bg-accent/30 transition-colors">
                                  <td className="px-4 py-3">
                                    <span className="font-medium text-foreground">{row.type}</span>
                                    <span className="text-xs text-muted-foreground ml-2">{row.thickness}</span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="font-mono text-muted-foreground">
                                      {row.aiLow.toLocaleString("cs")}–{row.aiHigh.toLocaleString("cs")} Kč/m²
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    {real ? (
                                      <span className="font-mono font-semibold text-foreground">
                                        {real.min.toLocaleString("cs")}–{real.max.toLocaleString("cs")} Kč/m²
                                      </span>
                                    ) : (
                                      <span className="text-xs text-muted-foreground italic flex items-center gap-1">
                                        <AlertTriangle className="w-3 h-3" /> Žádná data
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    {deviation !== null ? (
                                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                        Math.abs(deviation) <= 10 ? "bg-green-500/10 text-green-600" :
                                        Math.abs(deviation) <= 25 ? "bg-orange-400/10 text-orange-500" :
                                        "bg-destructive/10 text-destructive"
                                      }`}>
                                        {deviation > 0 ? "+" : ""}{deviation} %
                                      </span>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <span className="text-xs text-muted-foreground">{real?.count || 0}</span>
                                  </td>
                                </tr>
                              );
                            })}

                            {/* Show extra types from suppliers not in AI hardcoded */}
                            {insulationTypes
                              .filter(t => !["eps_white", "eps_grey", "mineral_wool", "vent_facade"].includes(t))
                              .map(type => {
                                const real = realByType(type);
                                if (!real) return null;
                                return (
                                  <tr key={type} className="hover:bg-accent/30 transition-colors bg-primary/5">
                                    <td className="px-4 py-3">
                                      <span className="font-medium text-foreground">{type}</span>
                                      <span className="text-xs text-primary ml-2">jen reálná data</span>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-muted-foreground italic">Nemá v AI</td>
                                    <td className="px-4 py-3">
                                      <span className="font-mono font-semibold text-foreground">
                                        {real.min.toLocaleString("cs")}–{real.max.toLocaleString("cs")} Kč/m²
                                      </span>
                                    </td>
                                    <td className="px-4 py-3">—</td>
                                    <td className="px-4 py-3 text-center text-xs text-muted-foreground">{real.count}</td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>

                      {/* Improvement tips */}
                      <div className="border border-border rounded-xl p-4 bg-muted/20 space-y-2">
                        <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-primary" /> Jak zlepšit přesnost AI
                        </h4>
                        <ul className="text-xs text-muted-foreground space-y-1.5 list-disc list-inside">
                          <li>Více dodavatelů s vyplněným ceníkem → přesnější průměry a rozsahy</li>
                          <li>Dodavatelé z různých regionů → AI může rozlišovat ceny podle lokality</li>
                          <li>Aktuální ceníky (≤6 měsíců) → eliminace zastaralých dat</li>
                          <li>Materiály bez reálných dat (označené „Žádná data") AI odhaduje hardcoded rozsahy ±15–35 %</li>
                          <li>Znalostní báze (záložka Znalosti AI) doplňuje technické detaily – lambda, spotřeba, rizika</li>
                        </ul>
                      </div>
                    </div>
                  );
                })()}

                {priceSubTab === "supplier_prices" && (
                  <div className="space-y-4">
                    {/* Stats cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {priceStats.map(stat => stat && (
                        <div key={stat.type} className="border border-border rounded-xl p-3 bg-muted/30">
                          <h4 className="text-xs text-muted-foreground font-medium mb-1">{stat.type}</h4>
                          <div className="flex items-baseline gap-2">
                            <span className="text-lg font-bold text-foreground">{stat.avg} Kč</span>
                            <span className="text-xs text-muted-foreground">průměr</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {stat.min}–{stat.max} Kč · {stat.count} dodavatelů
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-xs text-muted-foreground">Filtr:</span>
                      <select
                        value={priceFilterType}
                        onChange={(e) => setPriceFilterType(e.target.value)}
                        className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground"
                      >
                        <option value="all">Všechny materiály</option>
                        {insulationTypes.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <select
                        value={priceFilterRegion}
                        onChange={(e) => setPriceFilterRegion(e.target.value)}
                        className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground"
                      >
                        <option value="all">Všechny regiony</option>
                        {uniqueRegions.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                      <span className="text-xs text-muted-foreground ml-2">
                        {filteredPriceItems.length} položek
                      </span>
                    </div>

                    {/* Price items table */}
                    {filteredPriceItems.length === 0 ? (
                      <p className="text-muted-foreground text-center py-10">Žádné cenové položky</p>
                    ) : (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr className="text-left text-xs text-muted-foreground">
                              <th className="px-4 py-2 font-medium">Dodavatel</th>
                              <th className="px-4 py-2 font-medium">Materiál</th>
                              <th className="px-4 py-2 font-medium">Tloušťka</th>
                              <th className="px-4 py-2 font-medium">Cena/m²</th>
                              <th className="px-4 py-2 font-medium">Region</th>
                              <th className="px-4 py-2 font-medium">Stav</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {filteredPriceItems.map(item => {
                              const supplier = suppliers.find(s => s.id === item.supplier_id);
                              const supplierLocs = locationPrefs.filter(l => l.supplier_id === item.supplier_id);
                              const regionDisplay = supplierLocs.some(l => l.is_whole_country)
                                ? "Celá ČR"
                                : supplierLocs.map(l => l.label).join(", ") || "—";
                              return (
                                <tr key={item.id} className="hover:bg-accent/30 transition-colors">
                                  <td className="px-4 py-2.5">
                                    <span className="font-medium text-foreground">{supplier?.company_name || "—"}</span>
                                  </td>
                                  <td className="px-4 py-2.5">
                                    <span className="text-foreground">{item.insulation_type}</span>
                                  </td>
                                  <td className="px-4 py-2.5 text-muted-foreground">{item.thickness_cm} cm</td>
                                  <td className="px-4 py-2.5">
                                    <span className="font-mono font-semibold text-foreground">{item.price_per_m2.toLocaleString("cs-CZ")} Kč</span>
                                  </td>
                                  <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-1 text-muted-foreground">
                                      <MapPin className="w-3 h-3" />
                                      <span className="text-xs truncate max-w-[120px]">{regionDisplay}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2.5">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_active ? "bg-green-500/10 text-green-600" : "bg-muted text-muted-foreground"}`}>
                                      {item.is_active ? t("admin.status.active") : t("admin.status.inactive")}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {tab === "knowledge" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    Znalostní báze pro AI chat – tyto texty se automaticky přidávají do kontextu AI asistenta.
                  </p>
                  {!showNewKnowledge && (
                    <button
                      type="button"
                      onClick={() => setShowNewKnowledge(true)}
                      className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                    >
                      <Plus className="w-3.5 h-3.5" /> Přidat znalost
                    </button>
                  )}
                </div>

                {showNewKnowledge && (
                  <div className="border border-primary/30 rounded-xl p-4 bg-primary/5 space-y-3">
                    <div className="flex gap-3">
                      <input
                        value={newKnowledgeCategory}
                        onChange={e => setNewKnowledgeCategory(e.target.value)}
                        className="px-3 py-1.5 text-sm rounded-lg border border-border bg-background text-foreground w-40"
                        placeholder="Kategorie (např. fasády)"
                      />
                      <input
                        value={newKnowledgeTitle}
                        onChange={e => setNewKnowledgeTitle(e.target.value)}
                        className="px-3 py-1.5 text-sm rounded-lg border border-border bg-background text-foreground flex-1"
                        placeholder={t("admin.placeholder.knowledgeName")}
                      />
                    </div>
                    <div className="relative">
                      <textarea
                        value={newKnowledgeContent}
                        onChange={e => setNewKnowledgeContent(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground font-mono min-h-[300px] resize-y"
                        placeholder={t("admin.placeholder.knowledgeContent")}
                      />
                      {newKnowledgeContent.trim().length > 50 && (
                        <button
                          type="button"
                          onClick={handleClassifyKnowledge}
                          disabled={classifyingKnowledge}
                          className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 text-xs bg-primary/10 text-primary border border-primary/20 rounded-lg hover:bg-primary/20 disabled:opacity-50 transition-colors"
                        >
                          <Bot className="w-3.5 h-3.5" />
                          {classifyingKnowledge ? t("admin.label.analyzing") : t("admin.label.aiCategorize")}
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleCreateKnowledge}
                        disabled={!newKnowledgeTitle.trim() || !newKnowledgeContent.trim()}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50"
                      >
                        <Save className="w-3.5 h-3.5" /> Uložit znalost
                      </button>
                      <button
                        onClick={() => { setShowNewKnowledge(false); setNewKnowledgeCategory(""); setNewKnowledgeTitle(""); setNewKnowledgeContent(""); }}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-muted text-muted-foreground rounded-lg hover:bg-accent"
                      >
                        <X className="w-3.5 h-3.5" /> Zrušit
                      </button>
                    </div>
                  </div>
                )}

                {knowledge.length === 0 ? (
                  <p className="text-muted-foreground text-center py-10">{t("admin.empty.inquiries")}</p>
                ) : (
                  knowledge.map(entry => (
                    <div key={entry.id} className={`border rounded-xl p-4 transition-colors ${entry.is_active ? "border-border" : "border-border/50 opacity-60"}`}>
                      {editingKnowledge === entry.id ? (
                        <div className="space-y-3">
                          <div className="flex gap-3">
                            <input
                              value={editCategory}
                              onChange={e => setEditCategory(e.target.value)}
                              className="px-3 py-1.5 text-sm rounded-lg border border-border bg-background text-foreground w-40"
                              placeholder="Kategorie"
                            />
                            <input
                              value={editTitle}
                              onChange={e => setEditTitle(e.target.value)}
                              className="px-3 py-1.5 text-sm rounded-lg border border-border bg-background text-foreground flex-1"
                              placeholder="Název"
                            />
                          </div>
                          <textarea
                            value={editContent}
                            onChange={e => setEditContent(e.target.value)}
                            className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground font-mono min-h-[400px] resize-y"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveKnowledge(entry)}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                            >
                              <Save className="w-3.5 h-3.5" /> Uložit
                            </button>
                            <button
                              onClick={() => setEditingKnowledge(null)}
                              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-muted text-muted-foreground rounded-lg hover:bg-accent"
                            >
                              <X className="w-3.5 h-3.5" /> Zrušit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-mono">{entry.category}</span>
                              <h3 className="font-medium text-foreground text-sm">{entry.title}</h3>
                              {!entry.is_active && <span className="text-xs text-destructive">(neaktivní)</span>}
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setExpandedKnowledge(expandedKnowledge === entry.id ? null : entry.id)}
                                className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground"
                              >
                                {expandedKnowledge === entry.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingKnowledge(entry.id);
                                  setEditTitle(entry.title);
                                  setEditCategory(entry.category);
                                  setEditContent(entry.content);
                                }}
                                className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleToggleKnowledge(entry)}
                                className={`text-xs px-2 py-1 rounded-lg ${entry.is_active ? "bg-green-500/10 text-green-600 hover:bg-red-500/10 hover:text-red-600" : "bg-muted text-muted-foreground hover:bg-green-500/10 hover:text-green-600"}`}
                              >
                                {entry.is_active ? t("admin.status.active") : t("admin.status.inactive")}
                              </button>
                              <button onClick={() => handleDeleteKnowledge(entry.id)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {entry.content.slice(0, 150).replace(/[#*]/g, "")}…
                          </p>
                          {expandedKnowledge === entry.id && (
                            <pre className="mt-3 text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg overflow-auto max-h-96 whitespace-pre-wrap font-mono border border-border/50">
                              {entry.content}
                            </pre>
                          )}
                        </>
                      )}
                    </div>
                  ))
                )}

                {/* Učení AI – learnings s výběrem enginu */}
                <div className="border-t border-border pt-6 mt-6">
                  <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                    <Brain className="w-4 h-4 text-primary" /> Učení AI
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Opravy a doplňky, které AI zohledňuje v chatu. Lze přiřadit ke konkrétnímu enginu (Fasádnické práce / Sádrokarton) nebo „Všechny".
                  </p>
                  {!showNewLearning ? (
                    <button
                      type="button"
                      onClick={() => setShowNewLearning(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                    >
                      <Plus className="w-3.5 h-3.5" /> Přidat učení
                    </button>
                  ) : (
                    <div className="border border-border rounded-xl p-4 bg-muted/20 space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Typ</label>
                          <select
                            value={newLearningType}
                            onChange={e => setNewLearningType(e.target.value)}
                            className="w-full px-3 py-1.5 text-sm rounded-lg border border-border bg-background text-foreground"
                          >
                            <option value="correction">Oprava</option>
                            <option value="addition">Doplněk</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">Engine</label>
                          <select
                            value={newLearningEngineId}
                            onChange={e => setNewLearningEngineId(e.target.value)}
                            className="w-full px-3 py-1.5 text-sm rounded-lg border border-border bg-background text-foreground"
                          >
                            <option value="">{getEngineLabel(null)}</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Téma</label>
                        <input
                          value={newLearningTopic}
                          onChange={e => setNewLearningTopic(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm rounded-lg border border-border bg-background text-foreground"
                          placeholder={t("admin.placeholder.priceName")}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Obsah učení</label>
                        <textarea
                          value={newLearningContent}
                          onChange={e => setNewLearningContent(e.target.value)}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-border bg-background text-foreground min-h-[100px] resize-y"
                          placeholder="Text, který má AI používat…"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleCreateLearning}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90"
                        >
                          <Save className="w-3.5 h-3.5" /> Uložit
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowNewLearning(false); setNewLearningTopic(""); setNewLearningContent(""); setNewLearningEngineId(""); }}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-muted text-muted-foreground rounded-lg hover:bg-accent"
                        >
                          <X className="w-3.5 h-3.5" /> Zrušit
                        </button>
                      </div>
                    </div>
                  )}
                  {learnings.length > 0 && (
                    <div className="mt-4 border border-border rounded-xl overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr className="text-left text-xs text-muted-foreground">
                            <th className="px-4 py-2 font-medium">Téma</th>
                            <th className="px-4 py-2 font-medium">Typ</th>
                            <th className="px-4 py-2 font-medium">Engine</th>
                            <th className="px-4 py-2 font-medium">Stav</th>
                            <th className="px-4 py-2 w-20" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {learnings.map(l => (
                            <tr key={l.id} className="hover:bg-accent/30 transition-colors">
                              <td className="px-4 py-2.5 font-medium text-foreground">{l.topic}</td>
                              <td className="px-4 py-2.5 text-muted-foreground">{l.type}</td>
                              <td className="px-4 py-2.5 text-muted-foreground">{getEngineLabel(l.engine_id)}</td>
                              <td className="px-4 py-2.5">
                                <button
                                  type="button"
                                  onClick={() => handleToggleLearning(l)}
                                  className={`text-xs px-2 py-1 rounded-lg ${l.is_active ? "bg-green-500/10 text-green-600 hover:bg-red-500/10 hover:text-red-600" : "bg-muted text-muted-foreground hover:bg-green-500/10 hover:text-green-600"}`}
                                >
                                  {l.is_active ? t("admin.status.active") : t("admin.status.inactive")}
                                </button>
                              </td>
                              <td className="px-4 py-2.5">
                                <button type="button" onClick={() => handleDeleteLearning(l.id)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === "landing" && (() => {
              const activeServices = services.filter(s => s.status === "active");
              const candidateServices = services.filter(s => s.status === "candidate");
              const queuedServices = services.filter(s => s.status === "queued");
              const rejectedServices = services.filter(s => s.status === "rejected");
              const serviceMap = Object.fromEntries(services.map(s => [s.id, s]));
              return (
                <div className="space-y-6">
                  <p className="text-sm text-muted-foreground">
                    Živý přehled AI služeb aplikace. AI automaticky navrhuje nové kategorie z dotazů uživatelů — schvaluj nebo odmítej směry, kterými se aplikace vyvíjí.
                  </p>

                  {/* Kandidáti – signál nových směrů */}
                  {candidateServices.length > 0 && (
                    <div className="rounded-xl border border-amber-400/40 bg-amber-50/50 dark:bg-amber-950/10 p-4 space-y-3">
                      <h3 className="text-sm font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Návrhy nových směrů ({candidateServices.length})
                      </h3>
                      <p className="text-xs text-muted-foreground -mt-1">AI zjistila zájem o tato témata. Aktivuj = zapni chat + nacenění, nebo přesuň do fronty k pozdějšímu rozhodnutí.</p>
                      <div className="space-y-2">
                        {candidateServices.map(s => (
                          <div key={s.id} className="flex items-start justify-between gap-3 bg-background rounded-lg border border-border p-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm">{s.name}</span>
                                <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{s.slug}</span>
                                <span className="text-xs font-medium text-amber-600">{s.query_count}× {s.query_count === 1 ? "dotaz" : s.query_count < 5 ? "dotazy" : "dotazů"}</span>
                              </div>
                            </div>
                            <div className="flex gap-2 shrink-0 mt-0.5">
                              <button
                                onClick={() => handleServiceStatus(s.id, "active")}
                                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors"
                              >
                                <ThumbsUp className="w-3 h-3" /> Aktivovat
                              </button>
                              <button
                                onClick={() => handleServiceStatus(s.id, "queued")}
                                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-border text-muted-foreground hover:bg-muted transition-colors"
                              >
                                <LayoutList className="w-3 h-3" /> Do fronty
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fronta – odložené kandidáty k pozdějšímu rozhodnutí */}
                  {queuedServices.length > 0 && (
                    <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                      <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                        <LayoutList className="w-4 h-4" />
                        Fronta ({queuedServices.length})
                      </h3>
                      <div className="space-y-2">
                        {queuedServices.map(s => (
                          <div key={s.id} className="flex items-center justify-between gap-3 bg-background rounded-lg border border-border p-3">
                            <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                              <span className="font-medium text-sm">{s.name}</span>
                              <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{s.slug}</span>
                              <span className="text-xs text-muted-foreground">{s.query_count}× {s.query_count === 1 ? "dotaz" : s.query_count < 5 ? "dotazy" : "dotazů"}</span>
                            </div>
                            <button
                              onClick={() => handleServiceStatus(s.id, "active")}
                              className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors shrink-0"
                            >
                              <ThumbsUp className="w-3 h-3" /> Aktivovat
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Aktivní služby */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                        Aktivní služby ({activeServices.length})
                      </h3>
                      <button
                        onClick={() => setShowCreateService(v => !v)}
                        className="text-xs flex items-center gap-1 text-primary hover:underline"
                      >
                        <Plus className="w-3 h-3" /> Nová služba
                      </button>
                    </div>

                    {/* Formulář pro novou službu */}
                    {showCreateService && (
                      <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-muted-foreground">Název</label>
                            <input
                              className="w-full text-sm border border-border rounded px-2 py-1 bg-background"
                              placeholder="Střechy"
                              value={newSvcName}
                              onChange={e => {
                                setNewSvcName(e.target.value);
                                if (!newSvcSlug) setNewSvcSlug(normalizeServiceSlug(e.target.value));
                              }}
                            />
                          </div>
                          <div>
                            <label className="text-xs text-muted-foreground">Slug (URL)</label>
                            <input
                              className="w-full text-sm border border-border rounded px-2 py-1 bg-background font-mono"
                              placeholder="strechy"
                              value={newSvcSlug}
                              onChange={e => setNewSvcSlug(e.target.value)}
                              onBlur={e => setNewSvcSlug(normalizeServiceSlug(e.target.value))}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground">Klíčová slova (čárkami)</label>
                          <input
                            className="w-full text-sm border border-border rounded px-2 py-1 bg-background"
                            placeholder="střecha, krytina, oprava střechy"
                            value={newSvcKeywords}
                            onChange={e => setNewSvcKeywords(e.target.value)}
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => setShowCreateService(false)} className="text-xs text-muted-foreground hover:text-foreground">Zrušit</button>
                          <button
                            onClick={handleCreateService}
                            disabled={newSvcSaving}
                            className="text-xs bg-primary text-primary-foreground rounded px-3 py-1 disabled:opacity-50 flex items-center gap-1"
                          >
                            {newSvcSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                            Vytvořit
                          </button>
                        </div>
                      </div>
                    )}
                    {activeServices.length === 0 ? (
                      <p className="text-muted-foreground text-sm">Žádné aktivní služby</p>
                    ) : (
                      <div className="space-y-2">
                        {activeServices.map(s => {
                          const isFasadyService = isFacadeServiceSlug(s.slug);
                          const config = quoteConfigs.find(c => c.service_id === s.id);
                          const modules = isFasadyService
                            ? [
                                { key: "walls", icon: "🧱", label: "Stěny", badgeCls: "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800" },
                                { key: "material", icon: "📦", label: "Materiál", badgeCls: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800" },
                                { key: "quote_builder", icon: "🧮", label: "Kalkulace", badgeCls: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800" },
                              ]
                            : [
                                { key: "basic_inquiry", icon: "📋", label: "Poptávka", badgeCls: "bg-slate-100 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800" },
                                { key: "simple_quote", icon: "💰", label: "Nabídka", badgeCls: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800" },
                              ];
                          return (
                            <div
                              key={s.id}
                              onClick={() => navigate(`/admin-secret/service/${s.slug}`)}
                              className="rounded-lg border border-green-500/30 bg-green-50/40 dark:bg-green-950/10 p-3 cursor-pointer hover:bg-green-50/60 dark:hover:bg-green-950/20 transition-colors group"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-sm group-hover:text-primary transition-colors">{s.name}</span>
                                    <span className="font-mono text-xs text-muted-foreground">{s.slug}</span>
                                    {s.created_by_ai && (
                                      <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">AI</span>
                                    )}
                                    {config && (
                                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                                        config.status === "approved"
                                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800"
                                          : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                                      }`}>
                                        {config.status === "approved" ? t("admin.label.formApproved") : t("admin.label.formDraft")}
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                                    {modules.map(m => (
                                      <span key={m.key} className={`text-[10px] px-2 py-0.5 rounded-full border ${m.badgeCls}`}>
                                        {m.icon} {m.label}
                                      </span>
                                    ))}
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                                      🧠 {learnings.filter(l => resolveSlug(l.engine_id || "") === resolveSlug(s.slug)).length} znalostí
                                    </span>
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1 shrink-0">
                                  <span className="text-lg font-bold text-green-600 leading-none">{s.query_count}</span>
                                  <span className="text-xs text-muted-foreground">{s.query_count === 1 ? "dotaz" : s.query_count < 5 ? "dotazy" : "dotazů"}</span>
                                  <span className="text-[10px] text-primary opacity-0 group-hover:opacity-100 transition-opacity mt-1">Otevřít →</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Odmítnuté */}
                  {rejectedServices.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-muted-foreground/40 inline-block" />
                        Odmítnuté ({rejectedServices.length})
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {rejectedServices.map(s => (
                          <div key={s.id} className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs text-muted-foreground opacity-50 hover:opacity-80 transition-opacity">
                            <span className="line-through">{s.name}</span>
                            <button onClick={() => handleServiceStatus(s.id, "candidate")} className="hover:text-foreground" title="Obnovit jako kandidáta">
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Nerozpoznané dotazy — vyžadují admin review */}
                  {(() => {
                    const unreviewedDetections = serviceDetections.filter(
                      d => !d.admin_reviewed && (d.outcome === "uncertain" || d.outcome === "new_service_suggested")
                    );
                    if (unreviewedDetections.length === 0) return null;
                    return (
                      <div className="rounded-xl border border-orange-400/40 bg-orange-50/50 dark:bg-orange-950/10 p-4 space-y-3">
                        <h3 className="text-sm font-semibold text-orange-700 dark:text-orange-400 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Nerozpoznané dotazy ({unreviewedDetections.length})
                        </h3>
                        <p className="text-xs text-muted-foreground -mt-1">
                          Dotazy, které AI nedokázala spolehlivě přiřadit ke službě. Přiřaď k existující, vytvoř novou, nebo zamítni.
                        </p>
                        <div className="space-y-2">
                          {unreviewedDetections.slice(0, 30).map(d => (
                            <div key={d.id} className="bg-background rounded-lg border border-border p-3 space-y-2">
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate" title={d.query_text}>
                                    „{d.query_text}"
                                  </p>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                      d.outcome === "new_service_suggested"
                                        ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                                        : "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                                    }`}>
                                      {d.outcome === "new_service_suggested" ? t("admin.label.newServiceProposal") : t("admin.label.uncertainMatch")}
                                    </span>
                                    {d.ai_suggested_service_name && (
                                      <span className="text-[10px] text-purple-600 dark:text-purple-400">
                                        AI navrhuje: {d.ai_suggested_service_name}
                                      </span>
                                    )}
                                    {d.keyword_match_slug && (
                                      <span className="text-[10px] text-muted-foreground">
                                        KW: {d.keyword_match_slug} ({((d.keyword_match_confidence || 0) * 100).toFixed(0)}%)
                                      </span>
                                    )}
                                    {d.ai_match_slug && !d.ai_match_is_new && (
                                      <span className="text-[10px] text-muted-foreground">
                                        AI: {d.ai_match_slug} ({((d.ai_match_confidence || 0) * 100).toFixed(0)}%)
                                      </span>
                                    )}
                                    <span className="text-[10px] text-muted-foreground">
                                      {new Date(d.created_at).toLocaleString("cs")}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <select
                                  defaultValue=""
                                  onChange={(e) => {
                                    if (e.target.value) handleCorrectDetection(d.id, e.target.value);
                                  }}
                                  className="text-xs border border-border rounded px-1.5 py-1 bg-background text-foreground"
                                >
                                  <option value="">Přiřadit ke službě...</option>
                                  {services.filter(s => s.status === "active").map(s => (
                                    <option key={s.slug} value={s.slug}>{s.name} ({s.slug})</option>
                                  ))}
                                </select>
                                {d.outcome === "new_service_suggested" && (
                                  <button
                                    onClick={() => handleCreateServiceFromDetection(d.id, d.ai_suggested_service_name || d.query_text.slice(0, 50))}
                                    className="text-xs px-2 py-1 rounded border border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:hover:bg-purple-900/20"
                                  >
                                    + Nová služba
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDismissDetection(d.id)}
                                  className="text-xs px-2 py-1 rounded border border-border text-muted-foreground hover:bg-muted"
                                >
                                  Zamítnout
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Detekce — posledních 50 s metadata */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-foreground">Detekce dotazů ({serviceDetections.length})</h3>
                    {serviceDetections.length === 0 ? (
                      <p className="text-muted-foreground text-sm text-center py-6">Zatím žádné detekce</p>
                    ) : (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-muted/50 border-b border-border">
                              <th className="text-left p-3 font-medium">Čas</th>
                              <th className="text-left p-3 font-medium">Dotaz</th>
                              <th className="text-left p-3 font-medium">Detekce</th>
                              <th className="text-left p-3 font-medium">Služba</th>
                            </tr>
                          </thead>
                          <tbody>
                            {serviceDetections.slice(0, 50).map((d) => (
                              <tr key={d.id} className="border-b border-border last:border-0 hover:bg-accent/30">
                                <td className="p-3 text-muted-foreground whitespace-nowrap text-xs">
                                  {new Date(d.created_at).toLocaleString("cs")}
                                </td>
                                <td className="p-3 text-foreground max-w-xs truncate" title={d.query_text}>
                                  {d.query_text}
                                </td>
                                <td className="p-3">
                                  <div className="flex flex-col gap-0.5">
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full inline-block w-fit font-medium ${
                                      d.outcome === "matched" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                        : d.outcome === "new_service_suggested" ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                                        : "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                                    }`}>
                                      {d.outcome === "matched" ? t("admin.label.match") : d.outcome === "new_service_suggested" ? t("admin.label.new") : t("admin.label.uncertain")}
                                    </span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full inline-block w-fit ${
                                      d.routing_method === "keyword" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                        : d.routing_method === "ai" ? "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400"
                                        : d.routing_method === "keyword+ai" ? "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400"
                                        : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                                    }`}>
                                      {d.routing_method}
                                    </span>
                                    {d.keyword_match_slug && (
                                      <span className="text-[10px] text-muted-foreground">
                                        KW: {d.keyword_match_slug} ({((d.keyword_match_confidence || 0) * 100).toFixed(0)}%)
                                      </span>
                                    )}
                                    {d.ai_match_slug && (
                                      <span className="text-[10px] text-muted-foreground">
                                        AI: {d.ai_match_slug} ({((d.ai_match_confidence || 0) * 100).toFixed(0)}%)
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-3">
                                  {d.corrected_service_slug ? (
                                    <div className="flex flex-col gap-0.5">
                                      <span className="text-xs line-through text-muted-foreground">{d.final_service_slug || "—"}</span>
                                      <span className="text-xs font-medium text-green-600">{d.corrected_service_slug}</span>
                                    </div>
                                  ) : (
                                    <select
                                      value={d.final_service_slug || ""}
                                      onChange={(e) => {
                                        if (e.target.value && e.target.value !== d.final_service_slug) {
                                          handleCorrectDetection(d.id, e.target.value);
                                        }
                                      }}
                                      className="text-xs border border-border rounded px-1.5 py-1 bg-background text-foreground max-w-[120px]"
                                    >
                                      <option value="">—</option>
                                      {services.filter(s => s.status === "active").map(s => (
                                        <option key={s.slug} value={s.slug}>{s.slug}</option>
                                      ))}
                                    </select>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Legacy: Dotazy z úvodu */}
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-foreground">Dotazy z úvodu ({landingQueries.length})</h3>
                    {landingQueries.length === 0 ? (
                      <p className="text-muted-foreground text-sm text-center py-6">Zatím žádné dotazy z úvodu</p>
                    ) : (
                      <div className="border border-border rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-muted/50 border-b border-border">
                              <th className="text-left p-3 font-medium">Čas</th>
                              <th className="text-left p-3 font-medium">Dotaz</th>
                              <th className="text-left p-3 font-medium">Služba</th>
                            </tr>
                          </thead>
                          <tbody>
                            {landingQueries.slice(0, 50).map((q) => {
                              const svc = q.service_id ? serviceMap[q.service_id] : null;
                              return (
                                <tr key={q.id} className="border-b border-border last:border-0 hover:bg-accent/30">
                                  <td className="p-3 text-muted-foreground whitespace-nowrap text-xs">{new Date(q.created_at).toLocaleString("cs")}</td>
                                  <td className="p-3 text-foreground max-w-xs truncate" title={q.query_text}>{q.query_text}</td>
                                  <td className="p-3">
                                    <select
                                      value={q.service_id || ""}
                                      onChange={(e) => handleUpdateLandingQueryService(q.id, e.target.value || null)}
                                      className="text-xs border border-border rounded px-1.5 py-1 bg-background text-foreground max-w-[120px]"
                                    >
                                      <option value="">—</option>
                                      {services.map(s => (
                                        <option key={s.id} value={s.id}>{s.slug}</option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {tab === "subscriptions" && (
              <div className="space-y-6">
                {/* Overview cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="border border-border rounded-xl p-4 bg-background">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                      <Building2 className="w-3.5 h-3.5" /> Celkem dodavatelů
                    </div>
                    <p className="text-2xl font-bold text-foreground">{suppliers.length}</p>
                  </div>
                  <div className="border border-border rounded-xl p-4 bg-background">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                      <Users className="w-3.5 h-3.5" /> Free
                    </div>
                    <p className="text-2xl font-bold text-foreground">{suppliers.length - premiumSupplierIds.size}</p>
                  </div>
                  <div className="border border-border rounded-xl p-4 bg-background">
                    <div className="flex items-center gap-2 text-amber-600 text-xs mb-1">
                      <Crown className="w-3.5 h-3.5" /> Premium
                    </div>
                    <p className="text-2xl font-bold text-amber-600">{premiumSupplierIds.size}</p>
                  </div>
                  <div className="border border-border rounded-xl p-4 bg-background">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                      <Shield className="w-3.5 h-3.5" /> S výjimkami
                    </div>
                    <p className="text-2xl font-bold text-foreground">{suppliersWithOverrides.size}</p>
                  </div>
                </div>

                {/* Supplier subscription list */}
                <div className="border border-border rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-muted/30 border-b border-border">
                    <h3 className="text-sm font-semibold text-foreground">Dodavatelé — správa předplatného</h3>
                  </div>
                  <div className="divide-y divide-border">
                    {suppliers.map((s) => {
                      const planKey = getSupplierPlanKey(s.id);
                      const isPremium = planKey === "premium";
                      const sub = supplierSubscriptions.find(ss => ss.supplier_id === s.id);
                      const overrides = supplierFeatureOverrides.filter(o => o.supplier_id === s.id);
                      const isExpanded = expandedSubscriptionSupplier === s.id;

                      return (
                        <div key={s.id}>
                          <div className="flex items-center justify-between px-4 py-3 hover:bg-accent/30 transition-colors">
                            <div className="flex items-center gap-3 min-w-0">
                              <button
                                onClick={() => setExpandedSubscriptionSupplier(isExpanded ? null : s.id)}
                                className="text-muted-foreground hover:text-foreground"
                              >
                                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">{s.company_name}</p>
                                <p className="text-xs text-muted-foreground">{s.contact_email || "—"} · {s.region || "—"}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {overrides.length > 0 && (
                                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                  {overrides.length} výjim.
                                </span>
                              )}
                              {sub?.activated_at && (
                                <span className="text-xs text-muted-foreground hidden sm:inline">
                                  od {new Date(sub.activated_at).toLocaleDateString("cs")}
                                </span>
                              )}
                              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                                isPremium
                                  ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                  : "bg-muted text-muted-foreground"
                              }`}>
                                {isPremium ? "Premium" : "Zdarma"}
                              </span>
                              <button
                                onClick={() => handleSetSupplierPlan(s.id, isPremium ? "free" : "premium")}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  isPremium
                                    ? "text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                                    : "text-muted-foreground hover:bg-accent"
                                }`}
                                title={isPremium ? "Downgrade na Free" : "Upgrade na Premium"}
                              >
                                {isPremium ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                              </button>
                            </div>
                          </div>

                          {/* Expanded: feature overrides */}
                          {isExpanded && (
                            <div className="px-4 pb-4 pt-1 bg-muted/10">
                              <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">Feature výjimky</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                {subscriptionFeatures.map((feat) => {
                                  const override = overrides.find(o => o.feature_key === feat.key);
                                  const planHasFeature = isPremium;
                                  const effectivelyAllowed = override ? override.is_enabled : planHasFeature;

                                  return (
                                    <div
                                      key={feat.key}
                                      className={`flex items-center justify-between p-2.5 rounded-lg border text-xs ${
                                        override
                                          ? "border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-900/10"
                                          : "border-border bg-background"
                                      }`}
                                    >
                                      <div className="min-w-0">
                                        <span className={`font-medium ${effectivelyAllowed ? "text-foreground" : "text-muted-foreground"}`}>
                                          {feat.label}
                                        </span>
                                        {override && (
                                          <span className="ml-1.5 text-blue-500 text-[10px]">override</span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1">
                                        {override && (
                                          <button
                                            onClick={() => handleToggleFeatureOverride(s.id, feat.key, null)}
                                            className="text-muted-foreground hover:text-destructive p-0.5"
                                            title="Odstranit výjimku"
                                          >
                                            <X className="w-3 h-3" />
                                          </button>
                                        )}
                                        <button
                                          onClick={() => handleToggleFeatureOverride(s.id, feat.key, !effectivelyAllowed)}
                                          className={`p-0.5 rounded ${effectivelyAllowed ? "text-green-600" : "text-muted-foreground"}`}
                                          title={effectivelyAllowed ? "Zakázat" : "Povolit"}
                                        >
                                          {effectivelyAllowed
                                            ? <ToggleRight className="w-4 h-4" />
                                            : <ToggleLeft className="w-4 h-4" />
                                          }
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Feature catalog */}
                <div className="border border-border rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-muted/30 border-b border-border">
                    <h3 className="text-sm font-semibold text-foreground">Katalog feature</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/20">
                          <th className="p-3 text-left text-xs font-medium text-muted-foreground">Feature</th>
                          <th className="p-3 text-left text-xs font-medium text-muted-foreground">Klíč</th>
                          <th className="p-3 text-left text-xs font-medium text-muted-foreground">Kategorie</th>
                          <th className="p-3 text-left text-xs font-medium text-muted-foreground">Fallback</th>
                          <th className="p-3 text-left text-xs font-medium text-muted-foreground">Závislosti</th>
                          <th className="p-3 text-center text-xs font-medium text-muted-foreground">Aktivní</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscriptionFeatures.map((feat) => (
                          <tr key={feat.id} className="border-b border-border last:border-0 hover:bg-accent/30">
                            <td className="p-3">
                              <div>
                                <span className="font-medium text-foreground">{feat.label}</span>
                                {feat.description && (
                                  <p className="text-xs text-muted-foreground mt-0.5">{feat.description}</p>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-xs text-muted-foreground font-mono">{feat.key}</td>
                            <td className="p-3">
                              <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
                                {FEATURE_CATEGORIES[feat.category] || feat.category}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                feat.fallback_mode === "hidden" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" :
                                feat.fallback_mode === "locked" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                                "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                              }`}>
                                {feat.fallback_mode}
                              </span>
                            </td>
                            <td className="p-3 text-xs text-muted-foreground">
                              {feat.required_service && <span className="block">svc: {feat.required_service}</span>}
                              {feat.required_plugin && <span className="block">plg: {feat.required_plugin}</span>}
                              {!feat.required_service && !feat.required_plugin && "—"}
                            </td>
                            <td className="p-3 text-center">
                              <button
                                onClick={() => handleToggleFeatureActive(feat.id, !feat.is_active)}
                                className={feat.is_active ? "text-green-600" : "text-muted-foreground"}
                              >
                                {feat.is_active ? <ToggleRight className="w-5 h-5 mx-auto" /> : <ToggleLeft className="w-5 h-5 mx-auto" />}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Plans summary */}
                <div className="border border-border rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-muted/30 border-b border-border">
                    <h3 className="text-sm font-semibold text-foreground">Plány</h3>
                  </div>
                  <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {subscriptionPlans.map((plan) => {
                      const mappedFeatureIds = planFeatures.filter(pf => pf.plan_id === plan.id).map(pf => pf.feature_id);
                      const mappedFeatures = subscriptionFeatures.filter(f => mappedFeatureIds.includes(f.id));
                      const subCount = plan.key === "free"
                        ? suppliers.length - premiumSupplierIds.size
                        : supplierSubscriptions.filter(s => s.plan_id === plan.id && s.is_active).length;

                      return (
                        <div key={plan.id} className={`rounded-xl border p-4 ${
                          plan.key === "premium"
                            ? "border-amber-200 bg-amber-50/30 dark:border-amber-800/50 dark:bg-amber-900/10"
                            : "border-border bg-background"
                        }`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {plan.key === "premium" && <Crown className="w-4 h-4 text-amber-600" />}
                              <h4 className="font-semibold text-foreground">{plan.name}</h4>
                            </div>
                            <span className="text-xs text-muted-foreground">{subCount} dodavatelů</span>
                          </div>
                          {plan.description && (
                            <p className="text-xs text-muted-foreground mb-3">{plan.description}</p>
                          )}
                          {mappedFeatures.length > 0 ? (
                            <div className="space-y-1">
                              {mappedFeatures.map(f => (
                                <div key={f.id} className="flex items-center gap-2 text-xs text-foreground">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                  {f.label}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground italic">Žádné premium funkce</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

          </>
        )}
      </div>

      {/* Inquiry Detail Modal */}
      {selectedInquiry && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedInquiry(null)}>
          <div className="bg-background border border-border rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-foreground mb-2">{selectedInquiry.title}</h2>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              selectedInquiry.status === "open" ? "bg-green-500/10 text-green-600" : "bg-muted text-muted-foreground"
            }`}>
              {selectedInquiry.status}
            </span>
            <div className="mt-4 space-y-2 text-sm text-muted-foreground">
              {selectedInquiry.description?.split("\n").map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
            <div className="mt-4 text-xs text-muted-foreground space-y-1">
              <p>ID: {selectedInquiry.id}</p>
              <p>User: {selectedInquiry.user_id || selectedInquiry.guest_email || "—"}</p>
              <p>Region: {selectedInquiry.region || "—"}</p>
              <p>Vytvořeno: {new Date(selectedInquiry.created_at).toLocaleString("cs")}</p>
            </div>
            <button
              onClick={() => setSelectedInquiry(null)}
              className="mt-4 w-full py-2 text-sm font-medium bg-primary text-primary-foreground rounded-xl hover:opacity-90 transition-opacity"
            >
              Zavřít
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
